import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKFloatToRESTFloat } from '@wix/sdk-runtime/transformations/float';
import { transformRESTFloatToSDKFloat } from '@wix/sdk-runtime/transformations/float';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformSDKFieldMaskToRESTFieldMask } from '@wix/sdk-runtime/transformations/field-mask';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixInvoicesInvoicesV4InvoicesUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'www.wixapis.com': [
      {
        srcPath: '/invoices/v4/invoices',
        destPath: '/v4/invoices',
      },
      {
        srcPath: '/invoices/v4/bulk/invoices',
        destPath: '/v4/bulk/invoices',
      },
      {
        srcPath: '/invoices/v4/invoices-downloads',
        destPath: '/v4/invoices-downloads',
      },
    ],
    _: [
      {
        srcPath: '/invoices/v4/invoices',
        destPath: '/v4/invoices',
      },
      {
        srcPath: '/invoices/v4/bulk/invoices',
        destPath: '/v4/bulk/invoices',
      },
      {
        srcPath: '/invoices/v4/invoices-downloads',
        destPath: '/v4/invoices-downloads',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/invoices/v4/invoices',
        destPath: '/v4/invoices',
      },
      {
        srcPath: '/invoices/v4/bulk/invoices',
        destPath: '/v4/bulk/invoices',
      },
      {
        srcPath: '/invoices/v4/invoices-downloads',
        destPath: '/v4/invoices-downloads',
      },
    ],
    'manage._base_domain_': [
      {
        srcPath: '/_api/invoices-server/v4/invoices',
        destPath: '/v4/invoices',
      },
      {
        srcPath: '/_api/invoices-server/v4/bulk/invoices',
        destPath: '/v4/bulk/invoices',
      },
      {
        srcPath: '/_api/invoices-server/v4/invoices-downloads',
        destPath: '/v4/invoices-downloads',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_get-paid_invoices';

/**
 * Creates an invoice.
 *
 *
 * To create multiple invoices in a single API call, call [Bulk Create Invoices](https://dev.wix.com/docs/api-reference/business-management/get-paid/invoices/invoices/bulk-create-invoices).
 */
export function createInvoice(payload: object): RequestOptionsFactory<any> {
  function __createInvoice({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'invoice.createdDate' },
          { path: 'invoice.updatedDate' },
          { path: 'invoice.issueDate' },
          { path: 'invoice.dueDate' },
          { path: 'invoice.lineItems.customItem.subscriptionInfo.startDate' },
          { path: 'invoice.lineItems.catalogItem.subscriptionInfo.startDate' },
          { path: 'invoice.shipmentInfo.logistics.deliveryTimeSlot.from' },
          { path: 'invoice.shipmentInfo.logistics.deliveryTimeSlot.to' },
          { path: 'invoice.payments.createdDate' },
          { path: 'invoice.payments.updatedDate' },
          { path: 'invoice.payments.paymentDate' },
          {
            path: 'invoice.customFields.footerCustomField.content.metadata.createdTimestamp',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.metadata.updatedTimestamp',
          },
          { path: 'invoice.presetProperties.invoicePreset.createdDate' },
          { path: 'invoice.presetProperties.invoicePreset.updatedDate' },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.metadata.createdTimestamp',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.metadata.updatedTimestamp',
          },
          { path: 'invoice.documentInfo.documentExpirationDate' },
          { path: 'invoice.activityInfo.lastSentDate' },
          { path: 'invoice.activityInfo.lastViewedDate' },
        ],
      },
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'invoice.customerInfo.billingAddress.geocode.latitude' },
          { path: 'invoice.customerInfo.billingAddress.geocode.longitude' },
          { path: 'invoice.shippingInfo.shippingAddress.geocode.latitude' },
          { path: 'invoice.shippingInfo.shippingAddress.geocode.longitude' },
          { path: 'invoice.businessDetails.address.geocode.latitude' },
          { path: 'invoice.businessDetails.address.geocode.longitude' },
          {
            path: 'invoice.shipmentInfo.logistics.pickupDetails.address.geocode.latitude',
          },
          {
            path: 'invoice.shipmentInfo.logistics.pickupDetails.address.geocode.longitude',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.imageData.image.duration',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.imageData.crop.x',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.imageData.crop.y',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.imageData.crop.width',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.imageData.crop.height',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.pollData.poll.image.duration',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.appEmbedData.image.duration',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.videoData.video.duration',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.audioData.audio.duration',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.audioData.coverImage.duration',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.overlay.gradient.centerX',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.overlay.gradient.centerY',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.overlay.gradient.stops.position',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backdropImage.overlay.gradient.centerX',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backdropImage.overlay.gradient.centerY',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backdropImage.overlay.gradient.stops.position',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.shapeData.shape.duration',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.cardData.backgroundImage.overlay.gradient.centerX',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.cardData.backgroundImage.overlay.gradient.centerY',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.cardData.backgroundImage.overlay.gradient.stops.position',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.tocData.fontSize',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.tocData.itemSpacing',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.image.duration',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.x',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.y',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.width',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.height',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.pollData.poll.image.duration',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.appEmbedData.image.duration',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.videoData.video.duration',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.audioData.audio.duration',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.audioData.coverImage.duration',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.overlay.gradient.centerX',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.overlay.gradient.centerY',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.overlay.gradient.stops.position',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.overlay.gradient.centerX',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.overlay.gradient.centerY',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.overlay.gradient.stops.position',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.shapeData.shape.duration',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.overlay.gradient.centerX',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.overlay.gradient.centerY',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.overlay.gradient.stops.position',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.tocData.fontSize',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.tocData.itemSpacing',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.smartBlockCellData.shape.duration',
          },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.invoices.invoices.v4.invoice',
      method: 'POST' as any,
      methodFqn: 'wix.invoices.invoices.v4.Invoices.CreateInvoice',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixInvoicesInvoicesV4InvoicesUrl({
        protoPath: '/v4/invoices',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'invoice.createdDate' },
              { path: 'invoice.updatedDate' },
              { path: 'invoice.issueDate' },
              { path: 'invoice.dueDate' },
              {
                path: 'invoice.lineItems.customItem.subscriptionInfo.startDate',
              },
              {
                path: 'invoice.lineItems.catalogItem.subscriptionInfo.startDate',
              },
              { path: 'invoice.shipmentInfo.logistics.deliveryTimeSlot.from' },
              { path: 'invoice.shipmentInfo.logistics.deliveryTimeSlot.to' },
              { path: 'invoice.payments.createdDate' },
              { path: 'invoice.payments.updatedDate' },
              { path: 'invoice.payments.paymentDate' },
              {
                path: 'invoice.customFields.footerCustomField.content.metadata.createdTimestamp',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.metadata.updatedTimestamp',
              },
              { path: 'invoice.presetProperties.invoicePreset.createdDate' },
              { path: 'invoice.presetProperties.invoicePreset.updatedDate' },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.metadata.createdTimestamp',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.metadata.updatedTimestamp',
              },
              { path: 'invoice.documentInfo.documentExpirationDate' },
              { path: 'invoice.activityInfo.lastSentDate' },
              { path: 'invoice.activityInfo.lastViewedDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'invoice.customerInfo.billingAddress.geocode.latitude' },
              { path: 'invoice.customerInfo.billingAddress.geocode.longitude' },
              { path: 'invoice.shippingInfo.shippingAddress.geocode.latitude' },
              {
                path: 'invoice.shippingInfo.shippingAddress.geocode.longitude',
              },
              { path: 'invoice.businessDetails.address.geocode.latitude' },
              { path: 'invoice.businessDetails.address.geocode.longitude' },
              {
                path: 'invoice.shipmentInfo.logistics.pickupDetails.address.geocode.latitude',
              },
              {
                path: 'invoice.shipmentInfo.logistics.pickupDetails.address.geocode.longitude',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.imageData.image.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.imageData.crop.x',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.imageData.crop.y',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.imageData.crop.width',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.imageData.crop.height',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.pollData.poll.image.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.appEmbedData.image.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.videoData.video.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.audioData.audio.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.audioData.coverImage.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.overlay.gradient.centerX',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.overlay.gradient.centerY',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.overlay.gradient.stops.position',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backdropImage.overlay.gradient.centerX',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backdropImage.overlay.gradient.centerY',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backdropImage.overlay.gradient.stops.position',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.shapeData.shape.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.cardData.backgroundImage.overlay.gradient.centerX',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.cardData.backgroundImage.overlay.gradient.centerY',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.cardData.backgroundImage.overlay.gradient.stops.position',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.tocData.fontSize',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.tocData.itemSpacing',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.image.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.x',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.y',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.width',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.height',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.pollData.poll.image.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.appEmbedData.image.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.videoData.video.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.audioData.audio.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.audioData.coverImage.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.overlay.gradient.centerX',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.overlay.gradient.centerY',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.overlay.gradient.stops.position',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.overlay.gradient.centerX',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.overlay.gradient.centerY',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.overlay.gradient.stops.position',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.shapeData.shape.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.overlay.gradient.centerX',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.overlay.gradient.centerY',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.overlay.gradient.stops.position',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.tocData.fontSize',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.tocData.itemSpacing',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.smartBlockCellData.shape.duration',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __createInvoice;
}

/** Retrieves an invoice. */
export function getInvoice(payload: object): RequestOptionsFactory<any> {
  function __getInvoice({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.invoices.invoices.v4.invoice',
      method: 'GET' as any,
      methodFqn: 'wix.invoices.invoices.v4.Invoices.GetInvoice',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixInvoicesInvoicesV4InvoicesUrl({
        protoPath: '/v4/invoices/{invoiceId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'invoice.createdDate' },
              { path: 'invoice.updatedDate' },
              { path: 'invoice.issueDate' },
              { path: 'invoice.dueDate' },
              {
                path: 'invoice.lineItems.customItem.subscriptionInfo.startDate',
              },
              {
                path: 'invoice.lineItems.catalogItem.subscriptionInfo.startDate',
              },
              { path: 'invoice.shipmentInfo.logistics.deliveryTimeSlot.from' },
              { path: 'invoice.shipmentInfo.logistics.deliveryTimeSlot.to' },
              { path: 'invoice.payments.createdDate' },
              { path: 'invoice.payments.updatedDate' },
              { path: 'invoice.payments.paymentDate' },
              {
                path: 'invoice.customFields.footerCustomField.content.metadata.createdTimestamp',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.metadata.updatedTimestamp',
              },
              { path: 'invoice.presetProperties.invoicePreset.createdDate' },
              { path: 'invoice.presetProperties.invoicePreset.updatedDate' },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.metadata.createdTimestamp',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.metadata.updatedTimestamp',
              },
              { path: 'invoice.documentInfo.documentExpirationDate' },
              { path: 'invoice.activityInfo.lastSentDate' },
              { path: 'invoice.activityInfo.lastViewedDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'invoice.customerInfo.billingAddress.geocode.latitude' },
              { path: 'invoice.customerInfo.billingAddress.geocode.longitude' },
              { path: 'invoice.shippingInfo.shippingAddress.geocode.latitude' },
              {
                path: 'invoice.shippingInfo.shippingAddress.geocode.longitude',
              },
              { path: 'invoice.businessDetails.address.geocode.latitude' },
              { path: 'invoice.businessDetails.address.geocode.longitude' },
              {
                path: 'invoice.shipmentInfo.logistics.pickupDetails.address.geocode.latitude',
              },
              {
                path: 'invoice.shipmentInfo.logistics.pickupDetails.address.geocode.longitude',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.imageData.image.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.imageData.crop.x',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.imageData.crop.y',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.imageData.crop.width',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.imageData.crop.height',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.pollData.poll.image.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.appEmbedData.image.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.videoData.video.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.audioData.audio.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.audioData.coverImage.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.overlay.gradient.centerX',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.overlay.gradient.centerY',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.overlay.gradient.stops.position',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backdropImage.overlay.gradient.centerX',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backdropImage.overlay.gradient.centerY',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backdropImage.overlay.gradient.stops.position',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.shapeData.shape.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.cardData.backgroundImage.overlay.gradient.centerX',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.cardData.backgroundImage.overlay.gradient.centerY',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.cardData.backgroundImage.overlay.gradient.stops.position',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.tocData.fontSize',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.tocData.itemSpacing',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.image.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.x',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.y',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.width',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.height',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.pollData.poll.image.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.appEmbedData.image.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.videoData.video.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.audioData.audio.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.audioData.coverImage.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.overlay.gradient.centerX',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.overlay.gradient.centerY',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.overlay.gradient.stops.position',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.overlay.gradient.centerX',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.overlay.gradient.centerY',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.overlay.gradient.stops.position',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.shapeData.shape.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.overlay.gradient.centerX',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.overlay.gradient.centerY',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.overlay.gradient.stops.position',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.tocData.fontSize',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.tocData.itemSpacing',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.smartBlockCellData.shape.duration',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getInvoice;
}

/**
 * Updates an invoice.
 *
 *
 * To update multiple invoices in a single API call, call [Bulk Update Invoices](https://dev.wix.com/docs/api-reference/business-management/get-paid/invoices/invoices/bulk-update-invoices).
 */
export function updateInvoice(payload: object): RequestOptionsFactory<any> {
  function __updateInvoice({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'fieldMask' }],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'invoice.createdDate' },
          { path: 'invoice.updatedDate' },
          { path: 'invoice.issueDate' },
          { path: 'invoice.dueDate' },
          { path: 'invoice.lineItems.customItem.subscriptionInfo.startDate' },
          { path: 'invoice.lineItems.catalogItem.subscriptionInfo.startDate' },
          { path: 'invoice.shipmentInfo.logistics.deliveryTimeSlot.from' },
          { path: 'invoice.shipmentInfo.logistics.deliveryTimeSlot.to' },
          { path: 'invoice.payments.createdDate' },
          { path: 'invoice.payments.updatedDate' },
          { path: 'invoice.payments.paymentDate' },
          {
            path: 'invoice.customFields.footerCustomField.content.metadata.createdTimestamp',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.metadata.updatedTimestamp',
          },
          { path: 'invoice.presetProperties.invoicePreset.createdDate' },
          { path: 'invoice.presetProperties.invoicePreset.updatedDate' },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.metadata.createdTimestamp',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.metadata.updatedTimestamp',
          },
          { path: 'invoice.documentInfo.documentExpirationDate' },
          { path: 'invoice.activityInfo.lastSentDate' },
          { path: 'invoice.activityInfo.lastViewedDate' },
        ],
      },
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'invoice.customerInfo.billingAddress.geocode.latitude' },
          { path: 'invoice.customerInfo.billingAddress.geocode.longitude' },
          { path: 'invoice.shippingInfo.shippingAddress.geocode.latitude' },
          { path: 'invoice.shippingInfo.shippingAddress.geocode.longitude' },
          { path: 'invoice.businessDetails.address.geocode.latitude' },
          { path: 'invoice.businessDetails.address.geocode.longitude' },
          {
            path: 'invoice.shipmentInfo.logistics.pickupDetails.address.geocode.latitude',
          },
          {
            path: 'invoice.shipmentInfo.logistics.pickupDetails.address.geocode.longitude',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.imageData.image.duration',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.imageData.crop.x',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.imageData.crop.y',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.imageData.crop.width',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.imageData.crop.height',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.pollData.poll.image.duration',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.appEmbedData.image.duration',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.videoData.video.duration',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.audioData.audio.duration',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.audioData.coverImage.duration',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.overlay.gradient.centerX',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.overlay.gradient.centerY',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.overlay.gradient.stops.position',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backdropImage.overlay.gradient.centerX',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backdropImage.overlay.gradient.centerY',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backdropImage.overlay.gradient.stops.position',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.shapeData.shape.duration',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.cardData.backgroundImage.overlay.gradient.centerX',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.cardData.backgroundImage.overlay.gradient.centerY',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.cardData.backgroundImage.overlay.gradient.stops.position',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.tocData.fontSize',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.tocData.itemSpacing',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.image.duration',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.x',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.y',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.width',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.height',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.pollData.poll.image.duration',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.appEmbedData.image.duration',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.videoData.video.duration',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.audioData.audio.duration',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.audioData.coverImage.duration',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.overlay.gradient.centerX',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.overlay.gradient.centerY',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.overlay.gradient.stops.position',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.overlay.gradient.centerX',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.overlay.gradient.centerY',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.overlay.gradient.stops.position',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.shapeData.shape.duration',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.overlay.gradient.centerX',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.overlay.gradient.centerY',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.overlay.gradient.stops.position',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.tocData.fontSize',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.tocData.itemSpacing',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.smartBlockCellData.shape.duration',
          },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.invoices.invoices.v4.invoice',
      method: 'PATCH' as any,
      methodFqn: 'wix.invoices.invoices.v4.Invoices.UpdateInvoice',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixInvoicesInvoicesV4InvoicesUrl({
        protoPath: '/v4/invoices/{invoice.id}',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'invoice.createdDate' },
              { path: 'invoice.updatedDate' },
              { path: 'invoice.issueDate' },
              { path: 'invoice.dueDate' },
              {
                path: 'invoice.lineItems.customItem.subscriptionInfo.startDate',
              },
              {
                path: 'invoice.lineItems.catalogItem.subscriptionInfo.startDate',
              },
              { path: 'invoice.shipmentInfo.logistics.deliveryTimeSlot.from' },
              { path: 'invoice.shipmentInfo.logistics.deliveryTimeSlot.to' },
              { path: 'invoice.payments.createdDate' },
              { path: 'invoice.payments.updatedDate' },
              { path: 'invoice.payments.paymentDate' },
              {
                path: 'invoice.customFields.footerCustomField.content.metadata.createdTimestamp',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.metadata.updatedTimestamp',
              },
              { path: 'invoice.presetProperties.invoicePreset.createdDate' },
              { path: 'invoice.presetProperties.invoicePreset.updatedDate' },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.metadata.createdTimestamp',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.metadata.updatedTimestamp',
              },
              { path: 'invoice.documentInfo.documentExpirationDate' },
              { path: 'invoice.activityInfo.lastSentDate' },
              { path: 'invoice.activityInfo.lastViewedDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'invoice.customerInfo.billingAddress.geocode.latitude' },
              { path: 'invoice.customerInfo.billingAddress.geocode.longitude' },
              { path: 'invoice.shippingInfo.shippingAddress.geocode.latitude' },
              {
                path: 'invoice.shippingInfo.shippingAddress.geocode.longitude',
              },
              { path: 'invoice.businessDetails.address.geocode.latitude' },
              { path: 'invoice.businessDetails.address.geocode.longitude' },
              {
                path: 'invoice.shipmentInfo.logistics.pickupDetails.address.geocode.latitude',
              },
              {
                path: 'invoice.shipmentInfo.logistics.pickupDetails.address.geocode.longitude',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.imageData.image.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.imageData.crop.x',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.imageData.crop.y',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.imageData.crop.width',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.imageData.crop.height',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.pollData.poll.image.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.appEmbedData.image.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.videoData.video.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.audioData.audio.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.audioData.coverImage.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.overlay.gradient.centerX',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.overlay.gradient.centerY',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.overlay.gradient.stops.position',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backdropImage.overlay.gradient.centerX',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backdropImage.overlay.gradient.centerY',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backdropImage.overlay.gradient.stops.position',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.shapeData.shape.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.cardData.backgroundImage.overlay.gradient.centerX',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.cardData.backgroundImage.overlay.gradient.centerY',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.cardData.backgroundImage.overlay.gradient.stops.position',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.tocData.fontSize',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.tocData.itemSpacing',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.image.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.x',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.y',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.width',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.height',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.pollData.poll.image.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.appEmbedData.image.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.videoData.video.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.audioData.audio.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.audioData.coverImage.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.overlay.gradient.centerX',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.overlay.gradient.centerY',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.overlay.gradient.stops.position',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.overlay.gradient.centerX',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.overlay.gradient.centerY',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.overlay.gradient.stops.position',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.shapeData.shape.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.overlay.gradient.centerX',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.overlay.gradient.centerY',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.overlay.gradient.stops.position',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.tocData.fontSize',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.tocData.itemSpacing',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.smartBlockCellData.shape.duration',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __updateInvoice;
}

/**
 * Deletes an invoice.
 *
 *
 * Deleting an invoice permanently removes it from the invoice list. Only invoices in `DRAFT` status can be deleted.
 *
 * To delete multiple invoices in a single API call, call [Bulk Delete Invoices](https://dev.wix.com/docs/api-reference/business-management/get-paid/invoices/invoices/bulk-delete-invoices).
 */
export function deleteInvoice(payload: object): RequestOptionsFactory<any> {
  function __deleteInvoice({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.invoices.invoices.v4.invoice',
      method: 'DELETE' as any,
      methodFqn: 'wix.invoices.invoices.v4.Invoices.DeleteInvoice',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixInvoicesInvoicesV4InvoicesUrl({
        protoPath: '/v4/invoices/{invoiceId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __deleteInvoice;
}

/**
 * Retrieves a list of up to 1,000 invoices, given the provided paging, filtering, and sorting.
 *
 *
 * Query Invoices runs with these defaults, which you can override:
 *
 * - `createdDate` is sorted in `DESC` order
 * - `paging.limit` is `50`
 * - `paging.offset` is `0`
 *
 * To learn about working with Query methods, see [API Query Language](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-the-wix-api-query-language), [Sorting and Paging](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-sorting-and-paging), and [Field Projection](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-field-projection).
 */
export function queryInvoices(payload: object): RequestOptionsFactory<any> {
  function __queryInvoices({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.invoices.invoices.v4.invoice',
      method: 'GET' as any,
      methodFqn: 'wix.invoices.invoices.v4.Invoices.QueryInvoices',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixInvoicesInvoicesV4InvoicesUrl({
        protoPath: '/v4/invoices/query',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload, true),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'invoices.createdDate' },
              { path: 'invoices.updatedDate' },
              { path: 'invoices.issueDate' },
              { path: 'invoices.dueDate' },
              {
                path: 'invoices.lineItems.customItem.subscriptionInfo.startDate',
              },
              {
                path: 'invoices.lineItems.catalogItem.subscriptionInfo.startDate',
              },
              { path: 'invoices.shipmentInfo.logistics.deliveryTimeSlot.from' },
              { path: 'invoices.shipmentInfo.logistics.deliveryTimeSlot.to' },
              { path: 'invoices.payments.createdDate' },
              { path: 'invoices.payments.updatedDate' },
              { path: 'invoices.payments.paymentDate' },
              {
                path: 'invoices.customFields.footerCustomField.content.metadata.createdTimestamp',
              },
              {
                path: 'invoices.customFields.footerCustomField.content.metadata.updatedTimestamp',
              },
              { path: 'invoices.presetProperties.invoicePreset.createdDate' },
              { path: 'invoices.presetProperties.invoicePreset.updatedDate' },
              {
                path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.metadata.createdTimestamp',
              },
              {
                path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.metadata.updatedTimestamp',
              },
              { path: 'invoices.documentInfo.documentExpirationDate' },
              { path: 'invoices.activityInfo.lastSentDate' },
              { path: 'invoices.activityInfo.lastViewedDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'invoices.customerInfo.billingAddress.geocode.latitude' },
              {
                path: 'invoices.customerInfo.billingAddress.geocode.longitude',
              },
              {
                path: 'invoices.shippingInfo.shippingAddress.geocode.latitude',
              },
              {
                path: 'invoices.shippingInfo.shippingAddress.geocode.longitude',
              },
              { path: 'invoices.businessDetails.address.geocode.latitude' },
              { path: 'invoices.businessDetails.address.geocode.longitude' },
              {
                path: 'invoices.shipmentInfo.logistics.pickupDetails.address.geocode.latitude',
              },
              {
                path: 'invoices.shipmentInfo.logistics.pickupDetails.address.geocode.longitude',
              },
              {
                path: 'invoices.customFields.footerCustomField.content.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'invoices.customFields.footerCustomField.content.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'invoices.customFields.footerCustomField.content.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'invoices.customFields.footerCustomField.content.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'invoices.customFields.footerCustomField.content.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'invoices.customFields.footerCustomField.content.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'invoices.customFields.footerCustomField.content.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'invoices.customFields.footerCustomField.content.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'invoices.customFields.footerCustomField.content.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'invoices.customFields.footerCustomField.content.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'invoices.customFields.footerCustomField.content.nodes.imageData.image.duration',
              },
              {
                path: 'invoices.customFields.footerCustomField.content.nodes.imageData.crop.x',
              },
              {
                path: 'invoices.customFields.footerCustomField.content.nodes.imageData.crop.y',
              },
              {
                path: 'invoices.customFields.footerCustomField.content.nodes.imageData.crop.width',
              },
              {
                path: 'invoices.customFields.footerCustomField.content.nodes.imageData.crop.height',
              },
              {
                path: 'invoices.customFields.footerCustomField.content.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'invoices.customFields.footerCustomField.content.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'invoices.customFields.footerCustomField.content.nodes.pollData.poll.image.duration',
              },
              {
                path: 'invoices.customFields.footerCustomField.content.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'invoices.customFields.footerCustomField.content.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'invoices.customFields.footerCustomField.content.nodes.appEmbedData.image.duration',
              },
              {
                path: 'invoices.customFields.footerCustomField.content.nodes.videoData.video.duration',
              },
              {
                path: 'invoices.customFields.footerCustomField.content.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'invoices.customFields.footerCustomField.content.nodes.audioData.audio.duration',
              },
              {
                path: 'invoices.customFields.footerCustomField.content.nodes.audioData.coverImage.duration',
              },
              {
                path: 'invoices.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'invoices.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.overlay.gradient.centerX',
              },
              {
                path: 'invoices.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.overlay.gradient.centerY',
              },
              {
                path: 'invoices.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.overlay.gradient.stops.position',
              },
              {
                path: 'invoices.customFields.footerCustomField.content.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'invoices.customFields.footerCustomField.content.nodes.layoutData.backdropImage.overlay.gradient.centerX',
              },
              {
                path: 'invoices.customFields.footerCustomField.content.nodes.layoutData.backdropImage.overlay.gradient.centerY',
              },
              {
                path: 'invoices.customFields.footerCustomField.content.nodes.layoutData.backdropImage.overlay.gradient.stops.position',
              },
              {
                path: 'invoices.customFields.footerCustomField.content.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'invoices.customFields.footerCustomField.content.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'invoices.customFields.footerCustomField.content.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'invoices.customFields.footerCustomField.content.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'invoices.customFields.footerCustomField.content.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'invoices.customFields.footerCustomField.content.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'invoices.customFields.footerCustomField.content.nodes.shapeData.shape.duration',
              },
              {
                path: 'invoices.customFields.footerCustomField.content.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'invoices.customFields.footerCustomField.content.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'invoices.customFields.footerCustomField.content.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'invoices.customFields.footerCustomField.content.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'invoices.customFields.footerCustomField.content.nodes.cardData.backgroundImage.overlay.gradient.centerX',
              },
              {
                path: 'invoices.customFields.footerCustomField.content.nodes.cardData.backgroundImage.overlay.gradient.centerY',
              },
              {
                path: 'invoices.customFields.footerCustomField.content.nodes.cardData.backgroundImage.overlay.gradient.stops.position',
              },
              {
                path: 'invoices.customFields.footerCustomField.content.nodes.tocData.fontSize',
              },
              {
                path: 'invoices.customFields.footerCustomField.content.nodes.tocData.itemSpacing',
              },
              {
                path: 'invoices.customFields.footerCustomField.content.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.image.duration',
              },
              {
                path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.x',
              },
              {
                path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.y',
              },
              {
                path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.width',
              },
              {
                path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.height',
              },
              {
                path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.pollData.poll.image.duration',
              },
              {
                path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.appEmbedData.image.duration',
              },
              {
                path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.videoData.video.duration',
              },
              {
                path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.audioData.audio.duration',
              },
              {
                path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.audioData.coverImage.duration',
              },
              {
                path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.overlay.gradient.centerX',
              },
              {
                path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.overlay.gradient.centerY',
              },
              {
                path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.overlay.gradient.stops.position',
              },
              {
                path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.overlay.gradient.centerX',
              },
              {
                path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.overlay.gradient.centerY',
              },
              {
                path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.overlay.gradient.stops.position',
              },
              {
                path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.shapeData.shape.duration',
              },
              {
                path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.overlay.gradient.centerX',
              },
              {
                path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.overlay.gradient.centerY',
              },
              {
                path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.overlay.gradient.stops.position',
              },
              {
                path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.tocData.fontSize',
              },
              {
                path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.tocData.itemSpacing',
              },
              {
                path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.smartBlockCellData.shape.duration',
              },
            ],
          },
        ]),
      fallback: [
        {
          method: 'POST' as any,
          url: resolveWixInvoicesInvoicesV4InvoicesUrl({
            protoPath: '/v4/invoices/query',
            data: payload,
            host,
          }),
          data: payload,
        },
      ],
    };

    return metadata;
  }

  return __queryInvoices;
}

/**
 * Retrieves a list of up to 100 invoices, given the provided filtering, search expression, sorting, and cursor paging.
 *
 *
 * Unlike [Query Invoices], Search Invoices supports free-text search across fields like `title`, `numbering.displayNumber`, and customer contact details. It also returns aggregation data.
 */
export function searchInvoices(payload: object): RequestOptionsFactory<any> {
  function __searchInvoices({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'search.aggregations.range.buckets.from' },
          { path: 'search.aggregations.range.buckets.to' },
          {
            path: 'search.aggregations.nested.nestedAggregations.range.buckets.from',
          },
          {
            path: 'search.aggregations.nested.nestedAggregations.range.buckets.to',
          },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.invoices.invoices.v4.invoice',
      method: 'GET' as any,
      methodFqn: 'wix.invoices.invoices.v4.Invoices.SearchInvoices',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixInvoicesInvoicesV4InvoicesUrl({
        protoPath: '/v4/invoices/search',
        data: serializedData,
        host,
      }),
      params: toURLSearchParams(serializedData, true),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'invoices.createdDate' },
              { path: 'invoices.updatedDate' },
              { path: 'invoices.issueDate' },
              { path: 'invoices.dueDate' },
              {
                path: 'invoices.lineItems.customItem.subscriptionInfo.startDate',
              },
              {
                path: 'invoices.lineItems.catalogItem.subscriptionInfo.startDate',
              },
              { path: 'invoices.shipmentInfo.logistics.deliveryTimeSlot.from' },
              { path: 'invoices.shipmentInfo.logistics.deliveryTimeSlot.to' },
              { path: 'invoices.payments.createdDate' },
              { path: 'invoices.payments.updatedDate' },
              { path: 'invoices.payments.paymentDate' },
              {
                path: 'invoices.customFields.footerCustomField.content.metadata.createdTimestamp',
              },
              {
                path: 'invoices.customFields.footerCustomField.content.metadata.updatedTimestamp',
              },
              { path: 'invoices.presetProperties.invoicePreset.createdDate' },
              { path: 'invoices.presetProperties.invoicePreset.updatedDate' },
              {
                path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.metadata.createdTimestamp',
              },
              {
                path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.metadata.updatedTimestamp',
              },
              { path: 'invoices.documentInfo.documentExpirationDate' },
              { path: 'invoices.activityInfo.lastSentDate' },
              { path: 'invoices.activityInfo.lastViewedDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'invoices.customerInfo.billingAddress.geocode.latitude' },
              {
                path: 'invoices.customerInfo.billingAddress.geocode.longitude',
              },
              {
                path: 'invoices.shippingInfo.shippingAddress.geocode.latitude',
              },
              {
                path: 'invoices.shippingInfo.shippingAddress.geocode.longitude',
              },
              { path: 'invoices.businessDetails.address.geocode.latitude' },
              { path: 'invoices.businessDetails.address.geocode.longitude' },
              {
                path: 'invoices.shipmentInfo.logistics.pickupDetails.address.geocode.latitude',
              },
              {
                path: 'invoices.shipmentInfo.logistics.pickupDetails.address.geocode.longitude',
              },
              {
                path: 'invoices.customFields.footerCustomField.content.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'invoices.customFields.footerCustomField.content.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'invoices.customFields.footerCustomField.content.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'invoices.customFields.footerCustomField.content.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'invoices.customFields.footerCustomField.content.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'invoices.customFields.footerCustomField.content.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'invoices.customFields.footerCustomField.content.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'invoices.customFields.footerCustomField.content.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'invoices.customFields.footerCustomField.content.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'invoices.customFields.footerCustomField.content.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'invoices.customFields.footerCustomField.content.nodes.imageData.image.duration',
              },
              {
                path: 'invoices.customFields.footerCustomField.content.nodes.imageData.crop.x',
              },
              {
                path: 'invoices.customFields.footerCustomField.content.nodes.imageData.crop.y',
              },
              {
                path: 'invoices.customFields.footerCustomField.content.nodes.imageData.crop.width',
              },
              {
                path: 'invoices.customFields.footerCustomField.content.nodes.imageData.crop.height',
              },
              {
                path: 'invoices.customFields.footerCustomField.content.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'invoices.customFields.footerCustomField.content.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'invoices.customFields.footerCustomField.content.nodes.pollData.poll.image.duration',
              },
              {
                path: 'invoices.customFields.footerCustomField.content.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'invoices.customFields.footerCustomField.content.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'invoices.customFields.footerCustomField.content.nodes.appEmbedData.image.duration',
              },
              {
                path: 'invoices.customFields.footerCustomField.content.nodes.videoData.video.duration',
              },
              {
                path: 'invoices.customFields.footerCustomField.content.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'invoices.customFields.footerCustomField.content.nodes.audioData.audio.duration',
              },
              {
                path: 'invoices.customFields.footerCustomField.content.nodes.audioData.coverImage.duration',
              },
              {
                path: 'invoices.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'invoices.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.overlay.gradient.centerX',
              },
              {
                path: 'invoices.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.overlay.gradient.centerY',
              },
              {
                path: 'invoices.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.overlay.gradient.stops.position',
              },
              {
                path: 'invoices.customFields.footerCustomField.content.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'invoices.customFields.footerCustomField.content.nodes.layoutData.backdropImage.overlay.gradient.centerX',
              },
              {
                path: 'invoices.customFields.footerCustomField.content.nodes.layoutData.backdropImage.overlay.gradient.centerY',
              },
              {
                path: 'invoices.customFields.footerCustomField.content.nodes.layoutData.backdropImage.overlay.gradient.stops.position',
              },
              {
                path: 'invoices.customFields.footerCustomField.content.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'invoices.customFields.footerCustomField.content.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'invoices.customFields.footerCustomField.content.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'invoices.customFields.footerCustomField.content.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'invoices.customFields.footerCustomField.content.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'invoices.customFields.footerCustomField.content.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'invoices.customFields.footerCustomField.content.nodes.shapeData.shape.duration',
              },
              {
                path: 'invoices.customFields.footerCustomField.content.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'invoices.customFields.footerCustomField.content.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'invoices.customFields.footerCustomField.content.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'invoices.customFields.footerCustomField.content.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'invoices.customFields.footerCustomField.content.nodes.cardData.backgroundImage.overlay.gradient.centerX',
              },
              {
                path: 'invoices.customFields.footerCustomField.content.nodes.cardData.backgroundImage.overlay.gradient.centerY',
              },
              {
                path: 'invoices.customFields.footerCustomField.content.nodes.cardData.backgroundImage.overlay.gradient.stops.position',
              },
              {
                path: 'invoices.customFields.footerCustomField.content.nodes.tocData.fontSize',
              },
              {
                path: 'invoices.customFields.footerCustomField.content.nodes.tocData.itemSpacing',
              },
              {
                path: 'invoices.customFields.footerCustomField.content.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.image.duration',
              },
              {
                path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.x',
              },
              {
                path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.y',
              },
              {
                path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.width',
              },
              {
                path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.height',
              },
              {
                path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.pollData.poll.image.duration',
              },
              {
                path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.appEmbedData.image.duration',
              },
              {
                path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.videoData.video.duration',
              },
              {
                path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.audioData.audio.duration',
              },
              {
                path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.audioData.coverImage.duration',
              },
              {
                path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.overlay.gradient.centerX',
              },
              {
                path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.overlay.gradient.centerY',
              },
              {
                path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.overlay.gradient.stops.position',
              },
              {
                path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.overlay.gradient.centerX',
              },
              {
                path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.overlay.gradient.centerY',
              },
              {
                path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.overlay.gradient.stops.position',
              },
              {
                path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.shapeData.shape.duration',
              },
              {
                path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.overlay.gradient.centerX',
              },
              {
                path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.overlay.gradient.centerY',
              },
              {
                path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.overlay.gradient.stops.position',
              },
              {
                path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.tocData.fontSize',
              },
              {
                path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.tocData.itemSpacing',
              },
              {
                path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.smartBlockCellData.shape.duration',
              },
              { path: 'aggregationData.results.ranges.results.from' },
              { path: 'aggregationData.results.ranges.results.to' },
              {
                path: 'aggregationData.results.groupedByValue.results.nestedResults.ranges.results.from',
              },
              {
                path: 'aggregationData.results.groupedByValue.results.nestedResults.ranges.results.to',
              },
              {
                path: 'aggregationData.results.nested.results.results.*.range.from',
              },
              {
                path: 'aggregationData.results.nested.results.results.*.range.to',
              },
              { path: 'aggregationData.results.scalar.value' },
              {
                path: 'aggregationData.results.groupedByValue.results.nestedResults.scalar.value',
              },
              {
                path: 'aggregationData.results.nested.results.results.*.scalar.value',
              },
            ],
          },
        ]),
      fallback: [
        {
          method: 'POST' as any,
          url: resolveWixInvoicesInvoicesV4InvoicesUrl({
            protoPath: '/v4/invoices/search',
            data: serializedData,
            host,
          }),
          data: serializedData,
        },
      ],
    };

    return metadata;
  }

  return __searchInvoices;
}

/**
 * Sends an invoice to one or more contacts.
 *
 *
 * Specify `contactIds` in the request to choose recipients. If `contactIds` isn't provided, the invoice is sent to the customer contact on the invoice.
 */
export function sendInvoice(payload: object): RequestOptionsFactory<any> {
  function __sendInvoice({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.invoices.invoices.v4.invoice',
      method: 'POST' as any,
      methodFqn: 'wix.invoices.invoices.v4.Invoices.SendInvoice',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixInvoicesInvoicesV4InvoicesUrl({
        protoPath: '/v4/invoices/{invoiceId}/send',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'invoice.createdDate' },
              { path: 'invoice.updatedDate' },
              { path: 'invoice.issueDate' },
              { path: 'invoice.dueDate' },
              {
                path: 'invoice.lineItems.customItem.subscriptionInfo.startDate',
              },
              {
                path: 'invoice.lineItems.catalogItem.subscriptionInfo.startDate',
              },
              { path: 'invoice.shipmentInfo.logistics.deliveryTimeSlot.from' },
              { path: 'invoice.shipmentInfo.logistics.deliveryTimeSlot.to' },
              { path: 'invoice.payments.createdDate' },
              { path: 'invoice.payments.updatedDate' },
              { path: 'invoice.payments.paymentDate' },
              {
                path: 'invoice.customFields.footerCustomField.content.metadata.createdTimestamp',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.metadata.updatedTimestamp',
              },
              { path: 'invoice.presetProperties.invoicePreset.createdDate' },
              { path: 'invoice.presetProperties.invoicePreset.updatedDate' },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.metadata.createdTimestamp',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.metadata.updatedTimestamp',
              },
              { path: 'invoice.documentInfo.documentExpirationDate' },
              { path: 'invoice.activityInfo.lastSentDate' },
              { path: 'invoice.activityInfo.lastViewedDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'invoice.customerInfo.billingAddress.geocode.latitude' },
              { path: 'invoice.customerInfo.billingAddress.geocode.longitude' },
              { path: 'invoice.shippingInfo.shippingAddress.geocode.latitude' },
              {
                path: 'invoice.shippingInfo.shippingAddress.geocode.longitude',
              },
              { path: 'invoice.businessDetails.address.geocode.latitude' },
              { path: 'invoice.businessDetails.address.geocode.longitude' },
              {
                path: 'invoice.shipmentInfo.logistics.pickupDetails.address.geocode.latitude',
              },
              {
                path: 'invoice.shipmentInfo.logistics.pickupDetails.address.geocode.longitude',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.imageData.image.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.imageData.crop.x',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.imageData.crop.y',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.imageData.crop.width',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.imageData.crop.height',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.pollData.poll.image.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.appEmbedData.image.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.videoData.video.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.audioData.audio.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.audioData.coverImage.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.overlay.gradient.centerX',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.overlay.gradient.centerY',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.overlay.gradient.stops.position',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backdropImage.overlay.gradient.centerX',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backdropImage.overlay.gradient.centerY',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backdropImage.overlay.gradient.stops.position',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.shapeData.shape.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.cardData.backgroundImage.overlay.gradient.centerX',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.cardData.backgroundImage.overlay.gradient.centerY',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.cardData.backgroundImage.overlay.gradient.stops.position',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.tocData.fontSize',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.tocData.itemSpacing',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.image.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.x',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.y',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.width',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.height',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.pollData.poll.image.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.appEmbedData.image.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.videoData.video.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.audioData.audio.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.audioData.coverImage.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.overlay.gradient.centerX',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.overlay.gradient.centerY',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.overlay.gradient.stops.position',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.overlay.gradient.centerX',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.overlay.gradient.centerY',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.overlay.gradient.stops.position',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.shapeData.shape.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.overlay.gradient.centerX',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.overlay.gradient.centerY',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.overlay.gradient.stops.position',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.tocData.fontSize',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.tocData.itemSpacing',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.smartBlockCellData.shape.duration',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __sendInvoice;
}

/**
 * Publishes an invoice.
 *
 *
 * Transitions the invoice status from `DRAFT` to `PUBLISHED`, finalizing it and assigning an invoice number. For details on status transitions, see [Invoice Lifecycle](https://dev.wix.com/docs/api-reference/business-management/get-paid/invoices/standalone-invoice-payment-lifecycle).
 */
export function publishInvoice(payload: object): RequestOptionsFactory<any> {
  function __publishInvoice({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.invoices.invoices.v4.invoice',
      method: 'POST' as any,
      methodFqn: 'wix.invoices.invoices.v4.Invoices.PublishInvoice',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixInvoicesInvoicesV4InvoicesUrl({
        protoPath: '/v4/invoices/{invoiceId}/publish',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'invoice.createdDate' },
              { path: 'invoice.updatedDate' },
              { path: 'invoice.issueDate' },
              { path: 'invoice.dueDate' },
              {
                path: 'invoice.lineItems.customItem.subscriptionInfo.startDate',
              },
              {
                path: 'invoice.lineItems.catalogItem.subscriptionInfo.startDate',
              },
              { path: 'invoice.shipmentInfo.logistics.deliveryTimeSlot.from' },
              { path: 'invoice.shipmentInfo.logistics.deliveryTimeSlot.to' },
              { path: 'invoice.payments.createdDate' },
              { path: 'invoice.payments.updatedDate' },
              { path: 'invoice.payments.paymentDate' },
              {
                path: 'invoice.customFields.footerCustomField.content.metadata.createdTimestamp',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.metadata.updatedTimestamp',
              },
              { path: 'invoice.presetProperties.invoicePreset.createdDate' },
              { path: 'invoice.presetProperties.invoicePreset.updatedDate' },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.metadata.createdTimestamp',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.metadata.updatedTimestamp',
              },
              { path: 'invoice.documentInfo.documentExpirationDate' },
              { path: 'invoice.activityInfo.lastSentDate' },
              { path: 'invoice.activityInfo.lastViewedDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'invoice.customerInfo.billingAddress.geocode.latitude' },
              { path: 'invoice.customerInfo.billingAddress.geocode.longitude' },
              { path: 'invoice.shippingInfo.shippingAddress.geocode.latitude' },
              {
                path: 'invoice.shippingInfo.shippingAddress.geocode.longitude',
              },
              { path: 'invoice.businessDetails.address.geocode.latitude' },
              { path: 'invoice.businessDetails.address.geocode.longitude' },
              {
                path: 'invoice.shipmentInfo.logistics.pickupDetails.address.geocode.latitude',
              },
              {
                path: 'invoice.shipmentInfo.logistics.pickupDetails.address.geocode.longitude',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.imageData.image.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.imageData.crop.x',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.imageData.crop.y',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.imageData.crop.width',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.imageData.crop.height',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.pollData.poll.image.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.appEmbedData.image.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.videoData.video.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.audioData.audio.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.audioData.coverImage.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.overlay.gradient.centerX',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.overlay.gradient.centerY',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.overlay.gradient.stops.position',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backdropImage.overlay.gradient.centerX',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backdropImage.overlay.gradient.centerY',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backdropImage.overlay.gradient.stops.position',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.shapeData.shape.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.cardData.backgroundImage.overlay.gradient.centerX',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.cardData.backgroundImage.overlay.gradient.centerY',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.cardData.backgroundImage.overlay.gradient.stops.position',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.tocData.fontSize',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.tocData.itemSpacing',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.image.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.x',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.y',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.width',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.height',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.pollData.poll.image.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.appEmbedData.image.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.videoData.video.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.audioData.audio.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.audioData.coverImage.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.overlay.gradient.centerX',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.overlay.gradient.centerY',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.overlay.gradient.stops.position',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.overlay.gradient.centerX',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.overlay.gradient.centerY',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.overlay.gradient.stops.position',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.shapeData.shape.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.overlay.gradient.centerX',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.overlay.gradient.centerY',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.overlay.gradient.stops.position',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.tocData.fontSize',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.tocData.itemSpacing',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.smartBlockCellData.shape.duration',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __publishInvoice;
}

/**
 * Voids an invoice.
 *
 *
 * Transitions the invoice status to `VOIDED`, canceling it and making it no longer valid. Any payments made before voiding are preserved for the audit trail. For details on status transitions, see [Invoice Lifecycle](https://dev.wix.com/docs/api-reference/business-management/get-paid/invoices/standalone-invoice-payment-lifecycle).
 */
export function voidInvoice(payload: object): RequestOptionsFactory<any> {
  function __voidInvoice({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.invoices.invoices.v4.invoice',
      method: 'POST' as any,
      methodFqn: 'wix.invoices.invoices.v4.Invoices.VoidInvoice',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixInvoicesInvoicesV4InvoicesUrl({
        protoPath: '/v4/invoices/{invoiceId}/void',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'invoice.createdDate' },
              { path: 'invoice.updatedDate' },
              { path: 'invoice.issueDate' },
              { path: 'invoice.dueDate' },
              {
                path: 'invoice.lineItems.customItem.subscriptionInfo.startDate',
              },
              {
                path: 'invoice.lineItems.catalogItem.subscriptionInfo.startDate',
              },
              { path: 'invoice.shipmentInfo.logistics.deliveryTimeSlot.from' },
              { path: 'invoice.shipmentInfo.logistics.deliveryTimeSlot.to' },
              { path: 'invoice.payments.createdDate' },
              { path: 'invoice.payments.updatedDate' },
              { path: 'invoice.payments.paymentDate' },
              {
                path: 'invoice.customFields.footerCustomField.content.metadata.createdTimestamp',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.metadata.updatedTimestamp',
              },
              { path: 'invoice.presetProperties.invoicePreset.createdDate' },
              { path: 'invoice.presetProperties.invoicePreset.updatedDate' },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.metadata.createdTimestamp',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.metadata.updatedTimestamp',
              },
              { path: 'invoice.documentInfo.documentExpirationDate' },
              { path: 'invoice.activityInfo.lastSentDate' },
              { path: 'invoice.activityInfo.lastViewedDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'invoice.customerInfo.billingAddress.geocode.latitude' },
              { path: 'invoice.customerInfo.billingAddress.geocode.longitude' },
              { path: 'invoice.shippingInfo.shippingAddress.geocode.latitude' },
              {
                path: 'invoice.shippingInfo.shippingAddress.geocode.longitude',
              },
              { path: 'invoice.businessDetails.address.geocode.latitude' },
              { path: 'invoice.businessDetails.address.geocode.longitude' },
              {
                path: 'invoice.shipmentInfo.logistics.pickupDetails.address.geocode.latitude',
              },
              {
                path: 'invoice.shipmentInfo.logistics.pickupDetails.address.geocode.longitude',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.imageData.image.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.imageData.crop.x',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.imageData.crop.y',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.imageData.crop.width',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.imageData.crop.height',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.pollData.poll.image.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.appEmbedData.image.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.videoData.video.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.audioData.audio.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.audioData.coverImage.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.overlay.gradient.centerX',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.overlay.gradient.centerY',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.overlay.gradient.stops.position',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backdropImage.overlay.gradient.centerX',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backdropImage.overlay.gradient.centerY',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backdropImage.overlay.gradient.stops.position',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.shapeData.shape.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.cardData.backgroundImage.overlay.gradient.centerX',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.cardData.backgroundImage.overlay.gradient.centerY',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.cardData.backgroundImage.overlay.gradient.stops.position',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.tocData.fontSize',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.tocData.itemSpacing',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.image.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.x',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.y',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.width',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.height',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.pollData.poll.image.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.appEmbedData.image.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.videoData.video.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.audioData.audio.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.audioData.coverImage.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.overlay.gradient.centerX',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.overlay.gradient.centerY',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.overlay.gradient.stops.position',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.overlay.gradient.centerX',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.overlay.gradient.centerY',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.overlay.gradient.stops.position',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.shapeData.shape.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.overlay.gradient.centerX',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.overlay.gradient.centerY',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.overlay.gradient.stops.position',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.tocData.fontSize',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.tocData.itemSpacing',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.smartBlockCellData.shape.duration',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __voidInvoice;
}

/**
 * Archives an invoice.
 *
 *
 * Use archiving to filter and organize invoices. Archiving is available for all statuses except `DRAFT`.
 *
 * To remove an invoice from the archive, call [Unarchive Invoice](https://dev.wix.com/docs/api-reference/business-management/get-paid/invoices/invoices/unarchive-invoice).
 */
export function archiveInvoice(payload: object): RequestOptionsFactory<any> {
  function __archiveInvoice({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.invoices.invoices.v4.invoice',
      method: 'POST' as any,
      methodFqn: 'wix.invoices.invoices.v4.Invoices.ArchiveInvoice',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixInvoicesInvoicesV4InvoicesUrl({
        protoPath: '/v4/invoices/{invoiceId}/archive',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'invoice.createdDate' },
              { path: 'invoice.updatedDate' },
              { path: 'invoice.issueDate' },
              { path: 'invoice.dueDate' },
              {
                path: 'invoice.lineItems.customItem.subscriptionInfo.startDate',
              },
              {
                path: 'invoice.lineItems.catalogItem.subscriptionInfo.startDate',
              },
              { path: 'invoice.shipmentInfo.logistics.deliveryTimeSlot.from' },
              { path: 'invoice.shipmentInfo.logistics.deliveryTimeSlot.to' },
              { path: 'invoice.payments.createdDate' },
              { path: 'invoice.payments.updatedDate' },
              { path: 'invoice.payments.paymentDate' },
              {
                path: 'invoice.customFields.footerCustomField.content.metadata.createdTimestamp',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.metadata.updatedTimestamp',
              },
              { path: 'invoice.presetProperties.invoicePreset.createdDate' },
              { path: 'invoice.presetProperties.invoicePreset.updatedDate' },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.metadata.createdTimestamp',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.metadata.updatedTimestamp',
              },
              { path: 'invoice.documentInfo.documentExpirationDate' },
              { path: 'invoice.activityInfo.lastSentDate' },
              { path: 'invoice.activityInfo.lastViewedDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'invoice.customerInfo.billingAddress.geocode.latitude' },
              { path: 'invoice.customerInfo.billingAddress.geocode.longitude' },
              { path: 'invoice.shippingInfo.shippingAddress.geocode.latitude' },
              {
                path: 'invoice.shippingInfo.shippingAddress.geocode.longitude',
              },
              { path: 'invoice.businessDetails.address.geocode.latitude' },
              { path: 'invoice.businessDetails.address.geocode.longitude' },
              {
                path: 'invoice.shipmentInfo.logistics.pickupDetails.address.geocode.latitude',
              },
              {
                path: 'invoice.shipmentInfo.logistics.pickupDetails.address.geocode.longitude',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.imageData.image.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.imageData.crop.x',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.imageData.crop.y',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.imageData.crop.width',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.imageData.crop.height',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.pollData.poll.image.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.appEmbedData.image.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.videoData.video.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.audioData.audio.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.audioData.coverImage.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.overlay.gradient.centerX',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.overlay.gradient.centerY',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.overlay.gradient.stops.position',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backdropImage.overlay.gradient.centerX',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backdropImage.overlay.gradient.centerY',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backdropImage.overlay.gradient.stops.position',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.shapeData.shape.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.cardData.backgroundImage.overlay.gradient.centerX',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.cardData.backgroundImage.overlay.gradient.centerY',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.cardData.backgroundImage.overlay.gradient.stops.position',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.tocData.fontSize',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.tocData.itemSpacing',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.image.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.x',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.y',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.width',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.height',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.pollData.poll.image.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.appEmbedData.image.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.videoData.video.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.audioData.audio.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.audioData.coverImage.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.overlay.gradient.centerX',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.overlay.gradient.centerY',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.overlay.gradient.stops.position',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.overlay.gradient.centerX',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.overlay.gradient.centerY',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.overlay.gradient.stops.position',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.shapeData.shape.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.overlay.gradient.centerX',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.overlay.gradient.centerY',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.overlay.gradient.stops.position',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.tocData.fontSize',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.tocData.itemSpacing',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.smartBlockCellData.shape.duration',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __archiveInvoice;
}

/**
 * Unarchives an invoice.
 *
 *
 * Removes the invoice from the archived state. To archive an invoice, call [Archive Invoice](https://dev.wix.com/docs/api-reference/business-management/get-paid/invoices/invoices/archive-invoice).
 */
export function unarchiveInvoice(payload: object): RequestOptionsFactory<any> {
  function __unarchiveInvoice({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.invoices.invoices.v4.invoice',
      method: 'POST' as any,
      methodFqn: 'wix.invoices.invoices.v4.Invoices.UnarchiveInvoice',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixInvoicesInvoicesV4InvoicesUrl({
        protoPath: '/v4/invoices/{invoiceId}/unarchive',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'invoice.createdDate' },
              { path: 'invoice.updatedDate' },
              { path: 'invoice.issueDate' },
              { path: 'invoice.dueDate' },
              {
                path: 'invoice.lineItems.customItem.subscriptionInfo.startDate',
              },
              {
                path: 'invoice.lineItems.catalogItem.subscriptionInfo.startDate',
              },
              { path: 'invoice.shipmentInfo.logistics.deliveryTimeSlot.from' },
              { path: 'invoice.shipmentInfo.logistics.deliveryTimeSlot.to' },
              { path: 'invoice.payments.createdDate' },
              { path: 'invoice.payments.updatedDate' },
              { path: 'invoice.payments.paymentDate' },
              {
                path: 'invoice.customFields.footerCustomField.content.metadata.createdTimestamp',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.metadata.updatedTimestamp',
              },
              { path: 'invoice.presetProperties.invoicePreset.createdDate' },
              { path: 'invoice.presetProperties.invoicePreset.updatedDate' },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.metadata.createdTimestamp',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.metadata.updatedTimestamp',
              },
              { path: 'invoice.documentInfo.documentExpirationDate' },
              { path: 'invoice.activityInfo.lastSentDate' },
              { path: 'invoice.activityInfo.lastViewedDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'invoice.customerInfo.billingAddress.geocode.latitude' },
              { path: 'invoice.customerInfo.billingAddress.geocode.longitude' },
              { path: 'invoice.shippingInfo.shippingAddress.geocode.latitude' },
              {
                path: 'invoice.shippingInfo.shippingAddress.geocode.longitude',
              },
              { path: 'invoice.businessDetails.address.geocode.latitude' },
              { path: 'invoice.businessDetails.address.geocode.longitude' },
              {
                path: 'invoice.shipmentInfo.logistics.pickupDetails.address.geocode.latitude',
              },
              {
                path: 'invoice.shipmentInfo.logistics.pickupDetails.address.geocode.longitude',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.imageData.image.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.imageData.crop.x',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.imageData.crop.y',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.imageData.crop.width',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.imageData.crop.height',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.pollData.poll.image.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.appEmbedData.image.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.videoData.video.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.audioData.audio.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.audioData.coverImage.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.overlay.gradient.centerX',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.overlay.gradient.centerY',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.overlay.gradient.stops.position',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backdropImage.overlay.gradient.centerX',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backdropImage.overlay.gradient.centerY',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backdropImage.overlay.gradient.stops.position',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.shapeData.shape.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.cardData.backgroundImage.overlay.gradient.centerX',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.cardData.backgroundImage.overlay.gradient.centerY',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.cardData.backgroundImage.overlay.gradient.stops.position',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.tocData.fontSize',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.tocData.itemSpacing',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.image.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.x',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.y',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.width',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.height',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.pollData.poll.image.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.appEmbedData.image.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.videoData.video.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.audioData.audio.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.audioData.coverImage.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.overlay.gradient.centerX',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.overlay.gradient.centerY',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.overlay.gradient.stops.position',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.overlay.gradient.centerX',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.overlay.gradient.centerY',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.overlay.gradient.stops.position',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.shapeData.shape.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.overlay.gradient.centerX',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.overlay.gradient.centerY',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.overlay.gradient.stops.position',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.tocData.fontSize',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.tocData.itemSpacing',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.smartBlockCellData.shape.duration',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __unarchiveInvoice;
}

/**
 * Initiates payment for an invoice.
 *
 *
 * Creates a Wix eCommerce order if one doesn't exist, generates a payment request, and returns a checkout URL for the customer.
 */
export function initiatePayment(payload: object): RequestOptionsFactory<any> {
  function __initiatePayment({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.invoices.invoices.v4.invoice',
      method: 'POST' as any,
      methodFqn: 'wix.invoices.invoices.v4.Invoices.InitiatePayment',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixInvoicesInvoicesV4InvoicesUrl({
        protoPath: '/v4/invoices/{invoiceId}/initiate-payment',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __initiatePayment;
}

/**
 * Enables payments for an invoice.
 *
 *
 * Use this to record manual (offline) or MOTO (Mail Order/Telephone Order) payments against an invoice. Creates a Wix eCommerce order if one doesn't exist, which enables payment recording against the invoice. To generate a checkout URL for online payment instead, call [Initiate Payment](https://dev.wix.com/docs/api-reference/business-management/get-paid/invoices/invoices/initiate-payment).
 */
export function enableInvoicePayments(
  payload: object
): RequestOptionsFactory<any> {
  function __enableInvoicePayments({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.invoices.invoices.v4.invoice',
      method: 'POST' as any,
      methodFqn: 'wix.invoices.invoices.v4.Invoices.EnableInvoicePayments',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixInvoicesInvoicesV4InvoicesUrl({
        protoPath: '/v4/invoices/{invoiceId}/enable-payments',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __enableInvoicePayments;
}

/**
 * Generates a PDF document for an invoice.
 *
 *
 * PDF generation is asynchronous. Poll the invoice using [Get Invoice](https://dev.wix.com/docs/api-reference/business-management/get-paid/invoices/invoices/get-invoice) and check `documentInfo.status` until it's `AVAILABLE`, then use `documentInfo.downloadUrl` to access the file.
 */
export function generatePdfDocument(
  payload: object
): RequestOptionsFactory<any> {
  function __generatePdfDocument({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.invoices.invoices.v4.invoice',
      method: 'POST' as any,
      methodFqn: 'wix.invoices.invoices.v4.Invoices.GeneratePdfDocument',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixInvoicesInvoicesV4InvoicesUrl({
        protoPath: '/v4/invoices/{invoiceId}/generate-pdf-document',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'invoice.createdDate' },
              { path: 'invoice.updatedDate' },
              { path: 'invoice.issueDate' },
              { path: 'invoice.dueDate' },
              {
                path: 'invoice.lineItems.customItem.subscriptionInfo.startDate',
              },
              {
                path: 'invoice.lineItems.catalogItem.subscriptionInfo.startDate',
              },
              { path: 'invoice.shipmentInfo.logistics.deliveryTimeSlot.from' },
              { path: 'invoice.shipmentInfo.logistics.deliveryTimeSlot.to' },
              { path: 'invoice.payments.createdDate' },
              { path: 'invoice.payments.updatedDate' },
              { path: 'invoice.payments.paymentDate' },
              {
                path: 'invoice.customFields.footerCustomField.content.metadata.createdTimestamp',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.metadata.updatedTimestamp',
              },
              { path: 'invoice.presetProperties.invoicePreset.createdDate' },
              { path: 'invoice.presetProperties.invoicePreset.updatedDate' },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.metadata.createdTimestamp',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.metadata.updatedTimestamp',
              },
              { path: 'invoice.documentInfo.documentExpirationDate' },
              { path: 'invoice.activityInfo.lastSentDate' },
              { path: 'invoice.activityInfo.lastViewedDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'invoice.customerInfo.billingAddress.geocode.latitude' },
              { path: 'invoice.customerInfo.billingAddress.geocode.longitude' },
              { path: 'invoice.shippingInfo.shippingAddress.geocode.latitude' },
              {
                path: 'invoice.shippingInfo.shippingAddress.geocode.longitude',
              },
              { path: 'invoice.businessDetails.address.geocode.latitude' },
              { path: 'invoice.businessDetails.address.geocode.longitude' },
              {
                path: 'invoice.shipmentInfo.logistics.pickupDetails.address.geocode.latitude',
              },
              {
                path: 'invoice.shipmentInfo.logistics.pickupDetails.address.geocode.longitude',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.imageData.image.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.imageData.crop.x',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.imageData.crop.y',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.imageData.crop.width',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.imageData.crop.height',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.pollData.poll.image.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.appEmbedData.image.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.videoData.video.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.audioData.audio.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.audioData.coverImage.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.overlay.gradient.centerX',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.overlay.gradient.centerY',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.overlay.gradient.stops.position',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backdropImage.overlay.gradient.centerX',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backdropImage.overlay.gradient.centerY',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backdropImage.overlay.gradient.stops.position',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.shapeData.shape.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.cardData.backgroundImage.overlay.gradient.centerX',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.cardData.backgroundImage.overlay.gradient.centerY',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.cardData.backgroundImage.overlay.gradient.stops.position',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.tocData.fontSize',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.tocData.itemSpacing',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.image.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.x',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.y',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.width',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.height',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.pollData.poll.image.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.appEmbedData.image.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.videoData.video.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.audioData.audio.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.audioData.coverImage.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.overlay.gradient.centerX',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.overlay.gradient.centerY',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.overlay.gradient.stops.position',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.overlay.gradient.centerX',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.overlay.gradient.centerY',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.overlay.gradient.stops.position',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.shapeData.shape.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.overlay.gradient.centerX',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.overlay.gradient.centerY',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.overlay.gradient.stops.position',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.tocData.fontSize',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.tocData.itemSpacing',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.smartBlockCellData.shape.duration',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __generatePdfDocument;
}

/**
 * Marks an invoice as viewed.
 *
 *
 * Adds the `VIEWED` status qualifier to the invoice and records the view timestamp in `activityInfo`. For details, see [Status Qualifiers](https://dev.wix.com/docs/api-reference/business-management/get-paid/invoices/status-qualifiers).
 */
export function markInvoiceAsViewed(
  payload: object
): RequestOptionsFactory<any> {
  function __markInvoiceAsViewed({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.invoices.invoices.v4.invoice',
      method: 'POST' as any,
      methodFqn: 'wix.invoices.invoices.v4.Invoices.MarkInvoiceAsViewed',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixInvoicesInvoicesV4InvoicesUrl({
        protoPath: '/v4/invoices/{invoiceId}/mark-as-viewed',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'invoice.createdDate' },
              { path: 'invoice.updatedDate' },
              { path: 'invoice.issueDate' },
              { path: 'invoice.dueDate' },
              {
                path: 'invoice.lineItems.customItem.subscriptionInfo.startDate',
              },
              {
                path: 'invoice.lineItems.catalogItem.subscriptionInfo.startDate',
              },
              { path: 'invoice.shipmentInfo.logistics.deliveryTimeSlot.from' },
              { path: 'invoice.shipmentInfo.logistics.deliveryTimeSlot.to' },
              { path: 'invoice.payments.createdDate' },
              { path: 'invoice.payments.updatedDate' },
              { path: 'invoice.payments.paymentDate' },
              {
                path: 'invoice.customFields.footerCustomField.content.metadata.createdTimestamp',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.metadata.updatedTimestamp',
              },
              { path: 'invoice.presetProperties.invoicePreset.createdDate' },
              { path: 'invoice.presetProperties.invoicePreset.updatedDate' },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.metadata.createdTimestamp',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.metadata.updatedTimestamp',
              },
              { path: 'invoice.documentInfo.documentExpirationDate' },
              { path: 'invoice.activityInfo.lastSentDate' },
              { path: 'invoice.activityInfo.lastViewedDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'invoice.customerInfo.billingAddress.geocode.latitude' },
              { path: 'invoice.customerInfo.billingAddress.geocode.longitude' },
              { path: 'invoice.shippingInfo.shippingAddress.geocode.latitude' },
              {
                path: 'invoice.shippingInfo.shippingAddress.geocode.longitude',
              },
              { path: 'invoice.businessDetails.address.geocode.latitude' },
              { path: 'invoice.businessDetails.address.geocode.longitude' },
              {
                path: 'invoice.shipmentInfo.logistics.pickupDetails.address.geocode.latitude',
              },
              {
                path: 'invoice.shipmentInfo.logistics.pickupDetails.address.geocode.longitude',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.imageData.image.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.imageData.crop.x',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.imageData.crop.y',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.imageData.crop.width',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.imageData.crop.height',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.pollData.poll.image.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.appEmbedData.image.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.videoData.video.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.audioData.audio.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.audioData.coverImage.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.overlay.gradient.centerX',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.overlay.gradient.centerY',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.overlay.gradient.stops.position',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backdropImage.overlay.gradient.centerX',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backdropImage.overlay.gradient.centerY',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backdropImage.overlay.gradient.stops.position',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.shapeData.shape.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.cardData.backgroundImage.overlay.gradient.centerX',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.cardData.backgroundImage.overlay.gradient.centerY',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.cardData.backgroundImage.overlay.gradient.stops.position',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.tocData.fontSize',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.tocData.itemSpacing',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.image.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.x',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.y',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.width',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.height',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.pollData.poll.image.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.appEmbedData.image.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.videoData.video.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.audioData.audio.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.audioData.coverImage.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.overlay.gradient.centerX',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.overlay.gradient.centerY',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.overlay.gradient.stops.position',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.overlay.gradient.centerX',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.overlay.gradient.centerY',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.overlay.gradient.stops.position',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.shapeData.shape.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.overlay.gradient.centerX',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.overlay.gradient.centerY',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.overlay.gradient.stops.position',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.tocData.fontSize',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.tocData.itemSpacing',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.smartBlockCellData.shape.duration',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __markInvoiceAsViewed;
}

/**
 * Marks an invoice as sent.
 *
 *
 * Adds the `SENT` status qualifier to the invoice and records the send timestamp and recipient in `activityInfo`. For details, see [Status Qualifiers](https://dev.wix.com/docs/api-reference/business-management/get-paid/invoices/status-qualifiers).
 */
export function markInvoiceAsSent(payload: object): RequestOptionsFactory<any> {
  function __markInvoiceAsSent({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.invoices.invoices.v4.invoice',
      method: 'POST' as any,
      methodFqn: 'wix.invoices.invoices.v4.Invoices.MarkInvoiceAsSent',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixInvoicesInvoicesV4InvoicesUrl({
        protoPath: '/v4/invoices/{invoiceId}/mark-as-sent',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'invoice.createdDate' },
              { path: 'invoice.updatedDate' },
              { path: 'invoice.issueDate' },
              { path: 'invoice.dueDate' },
              {
                path: 'invoice.lineItems.customItem.subscriptionInfo.startDate',
              },
              {
                path: 'invoice.lineItems.catalogItem.subscriptionInfo.startDate',
              },
              { path: 'invoice.shipmentInfo.logistics.deliveryTimeSlot.from' },
              { path: 'invoice.shipmentInfo.logistics.deliveryTimeSlot.to' },
              { path: 'invoice.payments.createdDate' },
              { path: 'invoice.payments.updatedDate' },
              { path: 'invoice.payments.paymentDate' },
              {
                path: 'invoice.customFields.footerCustomField.content.metadata.createdTimestamp',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.metadata.updatedTimestamp',
              },
              { path: 'invoice.presetProperties.invoicePreset.createdDate' },
              { path: 'invoice.presetProperties.invoicePreset.updatedDate' },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.metadata.createdTimestamp',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.metadata.updatedTimestamp',
              },
              { path: 'invoice.documentInfo.documentExpirationDate' },
              { path: 'invoice.activityInfo.lastSentDate' },
              { path: 'invoice.activityInfo.lastViewedDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'invoice.customerInfo.billingAddress.geocode.latitude' },
              { path: 'invoice.customerInfo.billingAddress.geocode.longitude' },
              { path: 'invoice.shippingInfo.shippingAddress.geocode.latitude' },
              {
                path: 'invoice.shippingInfo.shippingAddress.geocode.longitude',
              },
              { path: 'invoice.businessDetails.address.geocode.latitude' },
              { path: 'invoice.businessDetails.address.geocode.longitude' },
              {
                path: 'invoice.shipmentInfo.logistics.pickupDetails.address.geocode.latitude',
              },
              {
                path: 'invoice.shipmentInfo.logistics.pickupDetails.address.geocode.longitude',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.imageData.image.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.imageData.crop.x',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.imageData.crop.y',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.imageData.crop.width',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.imageData.crop.height',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.pollData.poll.image.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.appEmbedData.image.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.videoData.video.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.audioData.audio.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.audioData.coverImage.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.overlay.gradient.centerX',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.overlay.gradient.centerY',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.overlay.gradient.stops.position',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backdropImage.overlay.gradient.centerX',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backdropImage.overlay.gradient.centerY',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backdropImage.overlay.gradient.stops.position',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.shapeData.shape.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.cardData.backgroundImage.overlay.gradient.centerX',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.cardData.backgroundImage.overlay.gradient.centerY',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.cardData.backgroundImage.overlay.gradient.stops.position',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.tocData.fontSize',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.tocData.itemSpacing',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.image.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.x',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.y',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.width',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.height',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.pollData.poll.image.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.appEmbedData.image.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.videoData.video.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.audioData.audio.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.audioData.coverImage.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.overlay.gradient.centerX',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.overlay.gradient.centerY',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.overlay.gradient.stops.position',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.overlay.gradient.centerX',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.overlay.gradient.centerY',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.overlay.gradient.stops.position',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.shapeData.shape.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.overlay.gradient.centerX',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.overlay.gradient.centerY',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.overlay.gradient.stops.position',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.tocData.fontSize',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.tocData.itemSpacing',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.smartBlockCellData.shape.duration',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __markInvoiceAsSent;
}

/**
 * Calculates totals, taxes, and available shipping options for a standalone invoice. Supports both custom and catalog line items.
 *
 *
 * Use this method before creating or updating an invoice to preview calculated values. Pass the returned `correlationId` in subsequent calculations to optimize incremental recalculations.
 */
export function calculateInvoice(payload: object): RequestOptionsFactory<any> {
  function __calculateInvoice({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'invoice.createdDate' },
          { path: 'invoice.updatedDate' },
          { path: 'invoice.issueDate' },
          { path: 'invoice.dueDate' },
          { path: 'invoice.lineItems.customItem.subscriptionInfo.startDate' },
          { path: 'invoice.lineItems.catalogItem.subscriptionInfo.startDate' },
          { path: 'invoice.shipmentInfo.logistics.deliveryTimeSlot.from' },
          { path: 'invoice.shipmentInfo.logistics.deliveryTimeSlot.to' },
          { path: 'invoice.payments.createdDate' },
          { path: 'invoice.payments.updatedDate' },
          { path: 'invoice.payments.paymentDate' },
          {
            path: 'invoice.customFields.footerCustomField.content.metadata.createdTimestamp',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.metadata.updatedTimestamp',
          },
          { path: 'invoice.presetProperties.invoicePreset.createdDate' },
          { path: 'invoice.presetProperties.invoicePreset.updatedDate' },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.metadata.createdTimestamp',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.metadata.updatedTimestamp',
          },
          { path: 'invoice.documentInfo.documentExpirationDate' },
          { path: 'invoice.activityInfo.lastSentDate' },
          { path: 'invoice.activityInfo.lastViewedDate' },
        ],
      },
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'invoice.customerInfo.billingAddress.geocode.latitude' },
          { path: 'invoice.customerInfo.billingAddress.geocode.longitude' },
          { path: 'invoice.shippingInfo.shippingAddress.geocode.latitude' },
          { path: 'invoice.shippingInfo.shippingAddress.geocode.longitude' },
          { path: 'invoice.businessDetails.address.geocode.latitude' },
          { path: 'invoice.businessDetails.address.geocode.longitude' },
          {
            path: 'invoice.shipmentInfo.logistics.pickupDetails.address.geocode.latitude',
          },
          {
            path: 'invoice.shipmentInfo.logistics.pickupDetails.address.geocode.longitude',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.imageData.image.duration',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.imageData.crop.x',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.imageData.crop.y',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.imageData.crop.width',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.imageData.crop.height',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.pollData.poll.image.duration',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.appEmbedData.image.duration',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.videoData.video.duration',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.audioData.audio.duration',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.audioData.coverImage.duration',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.overlay.gradient.centerX',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.overlay.gradient.centerY',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.overlay.gradient.stops.position',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backdropImage.overlay.gradient.centerX',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backdropImage.overlay.gradient.centerY',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backdropImage.overlay.gradient.stops.position',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.shapeData.shape.duration',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.cardData.backgroundImage.overlay.gradient.centerX',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.cardData.backgroundImage.overlay.gradient.centerY',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.cardData.backgroundImage.overlay.gradient.stops.position',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.tocData.fontSize',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.tocData.itemSpacing',
          },
          {
            path: 'invoice.customFields.footerCustomField.content.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.image.duration',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.x',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.y',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.width',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.height',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.pollData.poll.image.duration',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.appEmbedData.image.duration',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.videoData.video.duration',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.audioData.audio.duration',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.audioData.coverImage.duration',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.overlay.gradient.centerX',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.overlay.gradient.centerY',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.overlay.gradient.stops.position',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.overlay.gradient.centerX',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.overlay.gradient.centerY',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.overlay.gradient.stops.position',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.shapeData.shape.duration',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.overlay.gradient.centerX',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.overlay.gradient.centerY',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.overlay.gradient.stops.position',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.tocData.fontSize',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.tocData.itemSpacing',
          },
          {
            path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.smartBlockCellData.shape.duration',
          },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.invoices.invoices.v4.invoice',
      method: 'POST' as any,
      methodFqn: 'wix.invoices.invoices.v4.Invoices.CalculateInvoice',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixInvoicesInvoicesV4InvoicesUrl({
        protoPath: '/v4/invoices/calculate-invoice',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'invoice.createdDate' },
              { path: 'invoice.updatedDate' },
              { path: 'invoice.issueDate' },
              { path: 'invoice.dueDate' },
              {
                path: 'invoice.lineItems.customItem.subscriptionInfo.startDate',
              },
              {
                path: 'invoice.lineItems.catalogItem.subscriptionInfo.startDate',
              },
              { path: 'invoice.shipmentInfo.logistics.deliveryTimeSlot.from' },
              { path: 'invoice.shipmentInfo.logistics.deliveryTimeSlot.to' },
              { path: 'invoice.payments.createdDate' },
              { path: 'invoice.payments.updatedDate' },
              { path: 'invoice.payments.paymentDate' },
              {
                path: 'invoice.customFields.footerCustomField.content.metadata.createdTimestamp',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.metadata.updatedTimestamp',
              },
              { path: 'invoice.presetProperties.invoicePreset.createdDate' },
              { path: 'invoice.presetProperties.invoicePreset.updatedDate' },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.metadata.createdTimestamp',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.metadata.updatedTimestamp',
              },
              { path: 'invoice.documentInfo.documentExpirationDate' },
              { path: 'invoice.activityInfo.lastSentDate' },
              { path: 'invoice.activityInfo.lastViewedDate' },
              { path: 'shipmentOptions.logistics.deliveryTimeSlot.from' },
              { path: 'shipmentOptions.logistics.deliveryTimeSlot.to' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'invoice.customerInfo.billingAddress.geocode.latitude' },
              { path: 'invoice.customerInfo.billingAddress.geocode.longitude' },
              { path: 'invoice.shippingInfo.shippingAddress.geocode.latitude' },
              {
                path: 'invoice.shippingInfo.shippingAddress.geocode.longitude',
              },
              { path: 'invoice.businessDetails.address.geocode.latitude' },
              { path: 'invoice.businessDetails.address.geocode.longitude' },
              {
                path: 'invoice.shipmentInfo.logistics.pickupDetails.address.geocode.latitude',
              },
              {
                path: 'invoice.shipmentInfo.logistics.pickupDetails.address.geocode.longitude',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.imageData.image.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.imageData.crop.x',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.imageData.crop.y',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.imageData.crop.width',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.imageData.crop.height',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.pollData.poll.image.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.appEmbedData.image.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.videoData.video.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.audioData.audio.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.audioData.coverImage.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.overlay.gradient.centerX',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.overlay.gradient.centerY',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.overlay.gradient.stops.position',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backdropImage.overlay.gradient.centerX',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backdropImage.overlay.gradient.centerY',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backdropImage.overlay.gradient.stops.position',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.shapeData.shape.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.cardData.backgroundImage.overlay.gradient.centerX',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.cardData.backgroundImage.overlay.gradient.centerY',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.cardData.backgroundImage.overlay.gradient.stops.position',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.tocData.fontSize',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.tocData.itemSpacing',
              },
              {
                path: 'invoice.customFields.footerCustomField.content.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.image.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.x',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.y',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.width',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.height',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.pollData.poll.image.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.appEmbedData.image.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.videoData.video.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.audioData.audio.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.audioData.coverImage.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.overlay.gradient.centerX',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.overlay.gradient.centerY',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.overlay.gradient.stops.position',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.overlay.gradient.centerX',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.overlay.gradient.centerY',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.overlay.gradient.stops.position',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.shapeData.shape.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.overlay.gradient.centerX',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.overlay.gradient.centerY',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.overlay.gradient.stops.position',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.tocData.fontSize',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.tocData.itemSpacing',
              },
              {
                path: 'invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'shipmentOptions.logistics.pickupDetails.address.geocode.latitude',
              },
              {
                path: 'shipmentOptions.logistics.pickupDetails.address.geocode.longitude',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __calculateInvoice;
}

/**
 * Retrieves the highest invoice number currently in use.
 *
 *
 * Invoice numbering is managed in the [Invoices Settings API](https://dev.wix.com/docs/rest/business-management/get-paid/invoices/invoices-settings/introduction).
 */
export function getLatestInvoiceNumber(
  payload: object
): RequestOptionsFactory<any> {
  function __getLatestInvoiceNumber({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.invoices.invoices.v4.invoice',
      method: 'GET' as any,
      methodFqn: 'wix.invoices.invoices.v4.Invoices.GetLatestInvoiceNumber',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixInvoicesInvoicesV4InvoicesUrl({
        protoPath: '/v4/invoices/get-latest-number',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __getLatestInvoiceNumber;
}

/** Generates a receipt document for a specific payment on an invoice. Requires the `paymentId` of a confirmed payment on the invoice. */
export function generateReceipt(payload: object): RequestOptionsFactory<any> {
  function __generateReceipt({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.invoices.invoices.v4.invoice',
      method: 'POST' as any,
      methodFqn: 'wix.invoices.invoices.v4.Invoices.GenerateReceipt',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixInvoicesInvoicesV4InvoicesUrl({
        protoPath: '/v4/invoices/{invoiceId}/generate-receipt',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __generateReceipt;
}

/**
 * Creates multiple invoices.
 *
 *
 * To create a single invoice, call [Create Invoice](https://dev.wix.com/docs/api-reference/business-management/get-paid/invoices/invoices/create-invoice).
 */
export function bulkCreateInvoices(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkCreateInvoices({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'invoices.createdDate' },
          { path: 'invoices.updatedDate' },
          { path: 'invoices.issueDate' },
          { path: 'invoices.dueDate' },
          { path: 'invoices.lineItems.customItem.subscriptionInfo.startDate' },
          { path: 'invoices.lineItems.catalogItem.subscriptionInfo.startDate' },
          { path: 'invoices.shipmentInfo.logistics.deliveryTimeSlot.from' },
          { path: 'invoices.shipmentInfo.logistics.deliveryTimeSlot.to' },
          { path: 'invoices.payments.createdDate' },
          { path: 'invoices.payments.updatedDate' },
          { path: 'invoices.payments.paymentDate' },
          {
            path: 'invoices.customFields.footerCustomField.content.metadata.createdTimestamp',
          },
          {
            path: 'invoices.customFields.footerCustomField.content.metadata.updatedTimestamp',
          },
          { path: 'invoices.presetProperties.invoicePreset.createdDate' },
          { path: 'invoices.presetProperties.invoicePreset.updatedDate' },
          {
            path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.metadata.createdTimestamp',
          },
          {
            path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.metadata.updatedTimestamp',
          },
          { path: 'invoices.documentInfo.documentExpirationDate' },
          { path: 'invoices.activityInfo.lastSentDate' },
          { path: 'invoices.activityInfo.lastViewedDate' },
        ],
      },
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'invoices.customerInfo.billingAddress.geocode.latitude' },
          { path: 'invoices.customerInfo.billingAddress.geocode.longitude' },
          { path: 'invoices.shippingInfo.shippingAddress.geocode.latitude' },
          { path: 'invoices.shippingInfo.shippingAddress.geocode.longitude' },
          { path: 'invoices.businessDetails.address.geocode.latitude' },
          { path: 'invoices.businessDetails.address.geocode.longitude' },
          {
            path: 'invoices.shipmentInfo.logistics.pickupDetails.address.geocode.latitude',
          },
          {
            path: 'invoices.shipmentInfo.logistics.pickupDetails.address.geocode.longitude',
          },
          {
            path: 'invoices.customFields.footerCustomField.content.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'invoices.customFields.footerCustomField.content.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'invoices.customFields.footerCustomField.content.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'invoices.customFields.footerCustomField.content.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'invoices.customFields.footerCustomField.content.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'invoices.customFields.footerCustomField.content.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'invoices.customFields.footerCustomField.content.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'invoices.customFields.footerCustomField.content.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'invoices.customFields.footerCustomField.content.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'invoices.customFields.footerCustomField.content.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'invoices.customFields.footerCustomField.content.nodes.imageData.image.duration',
          },
          {
            path: 'invoices.customFields.footerCustomField.content.nodes.imageData.crop.x',
          },
          {
            path: 'invoices.customFields.footerCustomField.content.nodes.imageData.crop.y',
          },
          {
            path: 'invoices.customFields.footerCustomField.content.nodes.imageData.crop.width',
          },
          {
            path: 'invoices.customFields.footerCustomField.content.nodes.imageData.crop.height',
          },
          {
            path: 'invoices.customFields.footerCustomField.content.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'invoices.customFields.footerCustomField.content.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'invoices.customFields.footerCustomField.content.nodes.pollData.poll.image.duration',
          },
          {
            path: 'invoices.customFields.footerCustomField.content.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'invoices.customFields.footerCustomField.content.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'invoices.customFields.footerCustomField.content.nodes.appEmbedData.image.duration',
          },
          {
            path: 'invoices.customFields.footerCustomField.content.nodes.videoData.video.duration',
          },
          {
            path: 'invoices.customFields.footerCustomField.content.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'invoices.customFields.footerCustomField.content.nodes.audioData.audio.duration',
          },
          {
            path: 'invoices.customFields.footerCustomField.content.nodes.audioData.coverImage.duration',
          },
          {
            path: 'invoices.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'invoices.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.overlay.gradient.centerX',
          },
          {
            path: 'invoices.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.overlay.gradient.centerY',
          },
          {
            path: 'invoices.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.overlay.gradient.stops.position',
          },
          {
            path: 'invoices.customFields.footerCustomField.content.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'invoices.customFields.footerCustomField.content.nodes.layoutData.backdropImage.overlay.gradient.centerX',
          },
          {
            path: 'invoices.customFields.footerCustomField.content.nodes.layoutData.backdropImage.overlay.gradient.centerY',
          },
          {
            path: 'invoices.customFields.footerCustomField.content.nodes.layoutData.backdropImage.overlay.gradient.stops.position',
          },
          {
            path: 'invoices.customFields.footerCustomField.content.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'invoices.customFields.footerCustomField.content.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'invoices.customFields.footerCustomField.content.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'invoices.customFields.footerCustomField.content.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'invoices.customFields.footerCustomField.content.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'invoices.customFields.footerCustomField.content.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'invoices.customFields.footerCustomField.content.nodes.shapeData.shape.duration',
          },
          {
            path: 'invoices.customFields.footerCustomField.content.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'invoices.customFields.footerCustomField.content.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'invoices.customFields.footerCustomField.content.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'invoices.customFields.footerCustomField.content.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'invoices.customFields.footerCustomField.content.nodes.cardData.backgroundImage.overlay.gradient.centerX',
          },
          {
            path: 'invoices.customFields.footerCustomField.content.nodes.cardData.backgroundImage.overlay.gradient.centerY',
          },
          {
            path: 'invoices.customFields.footerCustomField.content.nodes.cardData.backgroundImage.overlay.gradient.stops.position',
          },
          {
            path: 'invoices.customFields.footerCustomField.content.nodes.tocData.fontSize',
          },
          {
            path: 'invoices.customFields.footerCustomField.content.nodes.tocData.itemSpacing',
          },
          {
            path: 'invoices.customFields.footerCustomField.content.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.image.duration',
          },
          {
            path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.x',
          },
          {
            path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.y',
          },
          {
            path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.width',
          },
          {
            path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.height',
          },
          {
            path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.pollData.poll.image.duration',
          },
          {
            path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.appEmbedData.image.duration',
          },
          {
            path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.videoData.video.duration',
          },
          {
            path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.audioData.audio.duration',
          },
          {
            path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.audioData.coverImage.duration',
          },
          {
            path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.overlay.gradient.centerX',
          },
          {
            path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.overlay.gradient.centerY',
          },
          {
            path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.overlay.gradient.stops.position',
          },
          {
            path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.overlay.gradient.centerX',
          },
          {
            path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.overlay.gradient.centerY',
          },
          {
            path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.overlay.gradient.stops.position',
          },
          {
            path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.shapeData.shape.duration',
          },
          {
            path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.overlay.gradient.centerX',
          },
          {
            path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.overlay.gradient.centerY',
          },
          {
            path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.overlay.gradient.stops.position',
          },
          {
            path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.tocData.fontSize',
          },
          {
            path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.tocData.itemSpacing',
          },
          {
            path: 'invoices.presetProperties.invoicePreset.customFields.footerCustomField.nodes.smartBlockCellData.shape.duration',
          },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.invoices.invoices.v4.invoice',
      method: 'POST' as any,
      methodFqn: 'wix.invoices.invoices.v4.Invoices.BulkCreateInvoices',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixInvoicesInvoicesV4InvoicesUrl({
        protoPath: '/v4/bulk/invoices/create',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'results.item.createdDate' },
              { path: 'results.item.updatedDate' },
              { path: 'results.item.issueDate' },
              { path: 'results.item.dueDate' },
              {
                path: 'results.item.lineItems.customItem.subscriptionInfo.startDate',
              },
              {
                path: 'results.item.lineItems.catalogItem.subscriptionInfo.startDate',
              },
              {
                path: 'results.item.shipmentInfo.logistics.deliveryTimeSlot.from',
              },
              {
                path: 'results.item.shipmentInfo.logistics.deliveryTimeSlot.to',
              },
              { path: 'results.item.payments.createdDate' },
              { path: 'results.item.payments.updatedDate' },
              { path: 'results.item.payments.paymentDate' },
              {
                path: 'results.item.customFields.footerCustomField.content.metadata.createdTimestamp',
              },
              {
                path: 'results.item.customFields.footerCustomField.content.metadata.updatedTimestamp',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.createdDate',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.updatedDate',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.customFields.footerCustomField.metadata.createdTimestamp',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.customFields.footerCustomField.metadata.updatedTimestamp',
              },
              { path: 'results.item.documentInfo.documentExpirationDate' },
              { path: 'results.item.activityInfo.lastSentDate' },
              { path: 'results.item.activityInfo.lastViewedDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'results.item.customerInfo.billingAddress.geocode.latitude',
              },
              {
                path: 'results.item.customerInfo.billingAddress.geocode.longitude',
              },
              {
                path: 'results.item.shippingInfo.shippingAddress.geocode.latitude',
              },
              {
                path: 'results.item.shippingInfo.shippingAddress.geocode.longitude',
              },
              { path: 'results.item.businessDetails.address.geocode.latitude' },
              {
                path: 'results.item.businessDetails.address.geocode.longitude',
              },
              {
                path: 'results.item.shipmentInfo.logistics.pickupDetails.address.geocode.latitude',
              },
              {
                path: 'results.item.shipmentInfo.logistics.pickupDetails.address.geocode.longitude',
              },
              {
                path: 'results.item.customFields.footerCustomField.content.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'results.item.customFields.footerCustomField.content.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'results.item.customFields.footerCustomField.content.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'results.item.customFields.footerCustomField.content.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'results.item.customFields.footerCustomField.content.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'results.item.customFields.footerCustomField.content.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'results.item.customFields.footerCustomField.content.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'results.item.customFields.footerCustomField.content.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'results.item.customFields.footerCustomField.content.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'results.item.customFields.footerCustomField.content.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'results.item.customFields.footerCustomField.content.nodes.imageData.image.duration',
              },
              {
                path: 'results.item.customFields.footerCustomField.content.nodes.imageData.crop.x',
              },
              {
                path: 'results.item.customFields.footerCustomField.content.nodes.imageData.crop.y',
              },
              {
                path: 'results.item.customFields.footerCustomField.content.nodes.imageData.crop.width',
              },
              {
                path: 'results.item.customFields.footerCustomField.content.nodes.imageData.crop.height',
              },
              {
                path: 'results.item.customFields.footerCustomField.content.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'results.item.customFields.footerCustomField.content.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'results.item.customFields.footerCustomField.content.nodes.pollData.poll.image.duration',
              },
              {
                path: 'results.item.customFields.footerCustomField.content.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'results.item.customFields.footerCustomField.content.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'results.item.customFields.footerCustomField.content.nodes.appEmbedData.image.duration',
              },
              {
                path: 'results.item.customFields.footerCustomField.content.nodes.videoData.video.duration',
              },
              {
                path: 'results.item.customFields.footerCustomField.content.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'results.item.customFields.footerCustomField.content.nodes.audioData.audio.duration',
              },
              {
                path: 'results.item.customFields.footerCustomField.content.nodes.audioData.coverImage.duration',
              },
              {
                path: 'results.item.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'results.item.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.overlay.gradient.centerX',
              },
              {
                path: 'results.item.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.overlay.gradient.centerY',
              },
              {
                path: 'results.item.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.overlay.gradient.stops.position',
              },
              {
                path: 'results.item.customFields.footerCustomField.content.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'results.item.customFields.footerCustomField.content.nodes.layoutData.backdropImage.overlay.gradient.centerX',
              },
              {
                path: 'results.item.customFields.footerCustomField.content.nodes.layoutData.backdropImage.overlay.gradient.centerY',
              },
              {
                path: 'results.item.customFields.footerCustomField.content.nodes.layoutData.backdropImage.overlay.gradient.stops.position',
              },
              {
                path: 'results.item.customFields.footerCustomField.content.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'results.item.customFields.footerCustomField.content.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'results.item.customFields.footerCustomField.content.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'results.item.customFields.footerCustomField.content.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'results.item.customFields.footerCustomField.content.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'results.item.customFields.footerCustomField.content.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'results.item.customFields.footerCustomField.content.nodes.shapeData.shape.duration',
              },
              {
                path: 'results.item.customFields.footerCustomField.content.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'results.item.customFields.footerCustomField.content.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'results.item.customFields.footerCustomField.content.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'results.item.customFields.footerCustomField.content.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'results.item.customFields.footerCustomField.content.nodes.cardData.backgroundImage.overlay.gradient.centerX',
              },
              {
                path: 'results.item.customFields.footerCustomField.content.nodes.cardData.backgroundImage.overlay.gradient.centerY',
              },
              {
                path: 'results.item.customFields.footerCustomField.content.nodes.cardData.backgroundImage.overlay.gradient.stops.position',
              },
              {
                path: 'results.item.customFields.footerCustomField.content.nodes.tocData.fontSize',
              },
              {
                path: 'results.item.customFields.footerCustomField.content.nodes.tocData.itemSpacing',
              },
              {
                path: 'results.item.customFields.footerCustomField.content.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.image.duration',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.x',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.y',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.width',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.height',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.customFields.footerCustomField.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.customFields.footerCustomField.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.customFields.footerCustomField.nodes.pollData.poll.image.duration',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.customFields.footerCustomField.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.customFields.footerCustomField.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.customFields.footerCustomField.nodes.appEmbedData.image.duration',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.customFields.footerCustomField.nodes.videoData.video.duration',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.customFields.footerCustomField.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.customFields.footerCustomField.nodes.audioData.audio.duration',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.customFields.footerCustomField.nodes.audioData.coverImage.duration',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.overlay.gradient.centerX',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.overlay.gradient.centerY',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.overlay.gradient.stops.position',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.overlay.gradient.centerX',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.overlay.gradient.centerY',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.overlay.gradient.stops.position',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.customFields.footerCustomField.nodes.shapeData.shape.duration',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.overlay.gradient.centerX',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.overlay.gradient.centerY',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.overlay.gradient.stops.position',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.customFields.footerCustomField.nodes.tocData.fontSize',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.customFields.footerCustomField.nodes.tocData.itemSpacing',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.customFields.footerCustomField.nodes.smartBlockCellData.shape.duration',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __bulkCreateInvoices;
}

/**
 * Updates multiple invoices.
 *
 *
 * To update a single invoice, call [Update Invoice](https://dev.wix.com/docs/api-reference/business-management/get-paid/invoices/invoices/update-invoice).
 */
export function bulkUpdateInvoices(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkUpdateInvoices({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'invoices.fieldMask' }],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'invoices.invoice.createdDate' },
          { path: 'invoices.invoice.updatedDate' },
          { path: 'invoices.invoice.issueDate' },
          { path: 'invoices.invoice.dueDate' },
          {
            path: 'invoices.invoice.lineItems.customItem.subscriptionInfo.startDate',
          },
          {
            path: 'invoices.invoice.lineItems.catalogItem.subscriptionInfo.startDate',
          },
          {
            path: 'invoices.invoice.shipmentInfo.logistics.deliveryTimeSlot.from',
          },
          {
            path: 'invoices.invoice.shipmentInfo.logistics.deliveryTimeSlot.to',
          },
          { path: 'invoices.invoice.payments.createdDate' },
          { path: 'invoices.invoice.payments.updatedDate' },
          { path: 'invoices.invoice.payments.paymentDate' },
          {
            path: 'invoices.invoice.customFields.footerCustomField.content.metadata.createdTimestamp',
          },
          {
            path: 'invoices.invoice.customFields.footerCustomField.content.metadata.updatedTimestamp',
          },
          {
            path: 'invoices.invoice.presetProperties.invoicePreset.createdDate',
          },
          {
            path: 'invoices.invoice.presetProperties.invoicePreset.updatedDate',
          },
          {
            path: 'invoices.invoice.presetProperties.invoicePreset.customFields.footerCustomField.metadata.createdTimestamp',
          },
          {
            path: 'invoices.invoice.presetProperties.invoicePreset.customFields.footerCustomField.metadata.updatedTimestamp',
          },
          { path: 'invoices.invoice.documentInfo.documentExpirationDate' },
          { path: 'invoices.invoice.activityInfo.lastSentDate' },
          { path: 'invoices.invoice.activityInfo.lastViewedDate' },
        ],
      },
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          {
            path: 'invoices.invoice.customerInfo.billingAddress.geocode.latitude',
          },
          {
            path: 'invoices.invoice.customerInfo.billingAddress.geocode.longitude',
          },
          {
            path: 'invoices.invoice.shippingInfo.shippingAddress.geocode.latitude',
          },
          {
            path: 'invoices.invoice.shippingInfo.shippingAddress.geocode.longitude',
          },
          { path: 'invoices.invoice.businessDetails.address.geocode.latitude' },
          {
            path: 'invoices.invoice.businessDetails.address.geocode.longitude',
          },
          {
            path: 'invoices.invoice.shipmentInfo.logistics.pickupDetails.address.geocode.latitude',
          },
          {
            path: 'invoices.invoice.shipmentInfo.logistics.pickupDetails.address.geocode.longitude',
          },
          {
            path: 'invoices.invoice.customFields.footerCustomField.content.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'invoices.invoice.customFields.footerCustomField.content.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'invoices.invoice.customFields.footerCustomField.content.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'invoices.invoice.customFields.footerCustomField.content.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'invoices.invoice.customFields.footerCustomField.content.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'invoices.invoice.customFields.footerCustomField.content.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'invoices.invoice.customFields.footerCustomField.content.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'invoices.invoice.customFields.footerCustomField.content.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'invoices.invoice.customFields.footerCustomField.content.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'invoices.invoice.customFields.footerCustomField.content.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'invoices.invoice.customFields.footerCustomField.content.nodes.imageData.image.duration',
          },
          {
            path: 'invoices.invoice.customFields.footerCustomField.content.nodes.imageData.crop.x',
          },
          {
            path: 'invoices.invoice.customFields.footerCustomField.content.nodes.imageData.crop.y',
          },
          {
            path: 'invoices.invoice.customFields.footerCustomField.content.nodes.imageData.crop.width',
          },
          {
            path: 'invoices.invoice.customFields.footerCustomField.content.nodes.imageData.crop.height',
          },
          {
            path: 'invoices.invoice.customFields.footerCustomField.content.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'invoices.invoice.customFields.footerCustomField.content.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'invoices.invoice.customFields.footerCustomField.content.nodes.pollData.poll.image.duration',
          },
          {
            path: 'invoices.invoice.customFields.footerCustomField.content.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'invoices.invoice.customFields.footerCustomField.content.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'invoices.invoice.customFields.footerCustomField.content.nodes.appEmbedData.image.duration',
          },
          {
            path: 'invoices.invoice.customFields.footerCustomField.content.nodes.videoData.video.duration',
          },
          {
            path: 'invoices.invoice.customFields.footerCustomField.content.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'invoices.invoice.customFields.footerCustomField.content.nodes.audioData.audio.duration',
          },
          {
            path: 'invoices.invoice.customFields.footerCustomField.content.nodes.audioData.coverImage.duration',
          },
          {
            path: 'invoices.invoice.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'invoices.invoice.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.overlay.gradient.centerX',
          },
          {
            path: 'invoices.invoice.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.overlay.gradient.centerY',
          },
          {
            path: 'invoices.invoice.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.overlay.gradient.stops.position',
          },
          {
            path: 'invoices.invoice.customFields.footerCustomField.content.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'invoices.invoice.customFields.footerCustomField.content.nodes.layoutData.backdropImage.overlay.gradient.centerX',
          },
          {
            path: 'invoices.invoice.customFields.footerCustomField.content.nodes.layoutData.backdropImage.overlay.gradient.centerY',
          },
          {
            path: 'invoices.invoice.customFields.footerCustomField.content.nodes.layoutData.backdropImage.overlay.gradient.stops.position',
          },
          {
            path: 'invoices.invoice.customFields.footerCustomField.content.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'invoices.invoice.customFields.footerCustomField.content.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'invoices.invoice.customFields.footerCustomField.content.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'invoices.invoice.customFields.footerCustomField.content.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'invoices.invoice.customFields.footerCustomField.content.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'invoices.invoice.customFields.footerCustomField.content.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'invoices.invoice.customFields.footerCustomField.content.nodes.shapeData.shape.duration',
          },
          {
            path: 'invoices.invoice.customFields.footerCustomField.content.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'invoices.invoice.customFields.footerCustomField.content.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'invoices.invoice.customFields.footerCustomField.content.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'invoices.invoice.customFields.footerCustomField.content.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'invoices.invoice.customFields.footerCustomField.content.nodes.cardData.backgroundImage.overlay.gradient.centerX',
          },
          {
            path: 'invoices.invoice.customFields.footerCustomField.content.nodes.cardData.backgroundImage.overlay.gradient.centerY',
          },
          {
            path: 'invoices.invoice.customFields.footerCustomField.content.nodes.cardData.backgroundImage.overlay.gradient.stops.position',
          },
          {
            path: 'invoices.invoice.customFields.footerCustomField.content.nodes.tocData.fontSize',
          },
          {
            path: 'invoices.invoice.customFields.footerCustomField.content.nodes.tocData.itemSpacing',
          },
          {
            path: 'invoices.invoice.customFields.footerCustomField.content.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'invoices.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'invoices.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'invoices.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'invoices.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'invoices.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'invoices.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'invoices.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'invoices.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'invoices.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'invoices.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'invoices.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.image.duration',
          },
          {
            path: 'invoices.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.x',
          },
          {
            path: 'invoices.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.y',
          },
          {
            path: 'invoices.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.width',
          },
          {
            path: 'invoices.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.height',
          },
          {
            path: 'invoices.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'invoices.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'invoices.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.pollData.poll.image.duration',
          },
          {
            path: 'invoices.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'invoices.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'invoices.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.appEmbedData.image.duration',
          },
          {
            path: 'invoices.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.videoData.video.duration',
          },
          {
            path: 'invoices.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'invoices.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.audioData.audio.duration',
          },
          {
            path: 'invoices.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.audioData.coverImage.duration',
          },
          {
            path: 'invoices.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'invoices.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.overlay.gradient.centerX',
          },
          {
            path: 'invoices.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.overlay.gradient.centerY',
          },
          {
            path: 'invoices.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.overlay.gradient.stops.position',
          },
          {
            path: 'invoices.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'invoices.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.overlay.gradient.centerX',
          },
          {
            path: 'invoices.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.overlay.gradient.centerY',
          },
          {
            path: 'invoices.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.overlay.gradient.stops.position',
          },
          {
            path: 'invoices.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'invoices.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'invoices.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'invoices.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'invoices.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'invoices.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'invoices.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.shapeData.shape.duration',
          },
          {
            path: 'invoices.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'invoices.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'invoices.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'invoices.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'invoices.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.overlay.gradient.centerX',
          },
          {
            path: 'invoices.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.overlay.gradient.centerY',
          },
          {
            path: 'invoices.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.overlay.gradient.stops.position',
          },
          {
            path: 'invoices.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.tocData.fontSize',
          },
          {
            path: 'invoices.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.tocData.itemSpacing',
          },
          {
            path: 'invoices.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.smartBlockCellData.shape.duration',
          },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.invoices.invoices.v4.invoice',
      method: 'POST' as any,
      methodFqn: 'wix.invoices.invoices.v4.Invoices.BulkUpdateInvoices',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixInvoicesInvoicesV4InvoicesUrl({
        protoPath: '/v4/bulk/invoices/update',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'results.item.createdDate' },
              { path: 'results.item.updatedDate' },
              { path: 'results.item.issueDate' },
              { path: 'results.item.dueDate' },
              {
                path: 'results.item.lineItems.customItem.subscriptionInfo.startDate',
              },
              {
                path: 'results.item.lineItems.catalogItem.subscriptionInfo.startDate',
              },
              {
                path: 'results.item.shipmentInfo.logistics.deliveryTimeSlot.from',
              },
              {
                path: 'results.item.shipmentInfo.logistics.deliveryTimeSlot.to',
              },
              { path: 'results.item.payments.createdDate' },
              { path: 'results.item.payments.updatedDate' },
              { path: 'results.item.payments.paymentDate' },
              {
                path: 'results.item.customFields.footerCustomField.content.metadata.createdTimestamp',
              },
              {
                path: 'results.item.customFields.footerCustomField.content.metadata.updatedTimestamp',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.createdDate',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.updatedDate',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.customFields.footerCustomField.metadata.createdTimestamp',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.customFields.footerCustomField.metadata.updatedTimestamp',
              },
              { path: 'results.item.documentInfo.documentExpirationDate' },
              { path: 'results.item.activityInfo.lastSentDate' },
              { path: 'results.item.activityInfo.lastViewedDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'results.item.customerInfo.billingAddress.geocode.latitude',
              },
              {
                path: 'results.item.customerInfo.billingAddress.geocode.longitude',
              },
              {
                path: 'results.item.shippingInfo.shippingAddress.geocode.latitude',
              },
              {
                path: 'results.item.shippingInfo.shippingAddress.geocode.longitude',
              },
              { path: 'results.item.businessDetails.address.geocode.latitude' },
              {
                path: 'results.item.businessDetails.address.geocode.longitude',
              },
              {
                path: 'results.item.shipmentInfo.logistics.pickupDetails.address.geocode.latitude',
              },
              {
                path: 'results.item.shipmentInfo.logistics.pickupDetails.address.geocode.longitude',
              },
              {
                path: 'results.item.customFields.footerCustomField.content.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'results.item.customFields.footerCustomField.content.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'results.item.customFields.footerCustomField.content.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'results.item.customFields.footerCustomField.content.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'results.item.customFields.footerCustomField.content.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'results.item.customFields.footerCustomField.content.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'results.item.customFields.footerCustomField.content.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'results.item.customFields.footerCustomField.content.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'results.item.customFields.footerCustomField.content.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'results.item.customFields.footerCustomField.content.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'results.item.customFields.footerCustomField.content.nodes.imageData.image.duration',
              },
              {
                path: 'results.item.customFields.footerCustomField.content.nodes.imageData.crop.x',
              },
              {
                path: 'results.item.customFields.footerCustomField.content.nodes.imageData.crop.y',
              },
              {
                path: 'results.item.customFields.footerCustomField.content.nodes.imageData.crop.width',
              },
              {
                path: 'results.item.customFields.footerCustomField.content.nodes.imageData.crop.height',
              },
              {
                path: 'results.item.customFields.footerCustomField.content.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'results.item.customFields.footerCustomField.content.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'results.item.customFields.footerCustomField.content.nodes.pollData.poll.image.duration',
              },
              {
                path: 'results.item.customFields.footerCustomField.content.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'results.item.customFields.footerCustomField.content.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'results.item.customFields.footerCustomField.content.nodes.appEmbedData.image.duration',
              },
              {
                path: 'results.item.customFields.footerCustomField.content.nodes.videoData.video.duration',
              },
              {
                path: 'results.item.customFields.footerCustomField.content.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'results.item.customFields.footerCustomField.content.nodes.audioData.audio.duration',
              },
              {
                path: 'results.item.customFields.footerCustomField.content.nodes.audioData.coverImage.duration',
              },
              {
                path: 'results.item.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'results.item.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.overlay.gradient.centerX',
              },
              {
                path: 'results.item.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.overlay.gradient.centerY',
              },
              {
                path: 'results.item.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.overlay.gradient.stops.position',
              },
              {
                path: 'results.item.customFields.footerCustomField.content.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'results.item.customFields.footerCustomField.content.nodes.layoutData.backdropImage.overlay.gradient.centerX',
              },
              {
                path: 'results.item.customFields.footerCustomField.content.nodes.layoutData.backdropImage.overlay.gradient.centerY',
              },
              {
                path: 'results.item.customFields.footerCustomField.content.nodes.layoutData.backdropImage.overlay.gradient.stops.position',
              },
              {
                path: 'results.item.customFields.footerCustomField.content.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'results.item.customFields.footerCustomField.content.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'results.item.customFields.footerCustomField.content.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'results.item.customFields.footerCustomField.content.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'results.item.customFields.footerCustomField.content.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'results.item.customFields.footerCustomField.content.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'results.item.customFields.footerCustomField.content.nodes.shapeData.shape.duration',
              },
              {
                path: 'results.item.customFields.footerCustomField.content.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'results.item.customFields.footerCustomField.content.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'results.item.customFields.footerCustomField.content.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'results.item.customFields.footerCustomField.content.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'results.item.customFields.footerCustomField.content.nodes.cardData.backgroundImage.overlay.gradient.centerX',
              },
              {
                path: 'results.item.customFields.footerCustomField.content.nodes.cardData.backgroundImage.overlay.gradient.centerY',
              },
              {
                path: 'results.item.customFields.footerCustomField.content.nodes.cardData.backgroundImage.overlay.gradient.stops.position',
              },
              {
                path: 'results.item.customFields.footerCustomField.content.nodes.tocData.fontSize',
              },
              {
                path: 'results.item.customFields.footerCustomField.content.nodes.tocData.itemSpacing',
              },
              {
                path: 'results.item.customFields.footerCustomField.content.nodes.smartBlockCellData.shape.duration',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.image.duration',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.x',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.y',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.width',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.height',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.customFields.footerCustomField.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.customFields.footerCustomField.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.customFields.footerCustomField.nodes.pollData.poll.image.duration',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.customFields.footerCustomField.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.customFields.footerCustomField.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.customFields.footerCustomField.nodes.appEmbedData.image.duration',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.customFields.footerCustomField.nodes.videoData.video.duration',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.customFields.footerCustomField.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.customFields.footerCustomField.nodes.audioData.audio.duration',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.customFields.footerCustomField.nodes.audioData.coverImage.duration',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.overlay.gradient.centerX',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.overlay.gradient.centerY',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.overlay.gradient.stops.position',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.overlay.gradient.centerX',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.overlay.gradient.centerY',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.overlay.gradient.stops.position',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.customFields.footerCustomField.nodes.shapeData.shape.duration',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.overlay.gradient.centerX',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.overlay.gradient.centerY',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.overlay.gradient.stops.position',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.customFields.footerCustomField.nodes.tocData.fontSize',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.customFields.footerCustomField.nodes.tocData.itemSpacing',
              },
              {
                path: 'results.item.presetProperties.invoicePreset.customFields.footerCustomField.nodes.smartBlockCellData.shape.duration',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __bulkUpdateInvoices;
}

/**
 * Deletes multiple invoices.
 *
 *
 * To delete a single invoice, call [Delete Invoice](https://dev.wix.com/docs/api-reference/business-management/get-paid/invoices/invoices/delete-invoice).
 */
export function bulkDeleteInvoices(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkDeleteInvoices({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.invoices.invoices.v4.invoice',
      method: 'POST' as any,
      methodFqn: 'wix.invoices.invoices.v4.Invoices.BulkDeleteInvoices',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixInvoicesInvoicesV4InvoicesUrl({
        protoPath: '/v4/bulk/invoices/delete',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __bulkDeleteInvoices;
}

/**
 * Adds or removes tags on multiple invoices. Tags are labels used to organize and filter invoices — they can be public (visible to anyone with access to the invoice) or private (restricted to site owners).
 *
 * Unlike [Bulk Update Invoices](https://dev.wix.com/docs/api-reference/business-management/get-paid/invoices/invoices/bulk-update-invoices), which replaces fields entirely, this method lets you specify individual tags to assign or unassign without affecting other existing tags. A tag that appears in both the list to assign and the list to unassign is assigned. Tags that don't exist are ignored.
 *
 * To update tags on all invoices matching a filter, call [Bulk Update Invoice Tags By Filter](https://dev.wix.com/docs/api-reference/business-management/get-paid/invoices/invoices/bulk-update-invoice-tags-by-filter).
 */
export function bulkUpdateInvoiceTags(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkUpdateInvoiceTags({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.invoices.invoices.v4.invoice',
      method: 'POST' as any,
      methodFqn: 'wix.invoices.invoices.v4.Invoices.BulkUpdateInvoiceTags',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixInvoicesInvoicesV4InvoicesUrl({
        protoPath: '/v4/bulk/invoices/update-tags',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __bulkUpdateInvoiceTags;
}

/**
 * Asynchronously updates tags on multiple invoices that match the provided filter criteria.
 *
 *
 * To check the status of the bulk update, pass the returned `jobId` to [Get Async Job](https://dev.wix.com/docs/api-reference/business-management/async-job/get-async-job).
 *
 * To update tags on specific invoices by ID, call [Bulk Update Invoice Tags](https://dev.wix.com/docs/api-reference/business-management/get-paid/invoices/invoices/bulk-update-invoice-tags).
 */
export function bulkUpdateInvoiceTagsByFilter(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkUpdateInvoiceTagsByFilter({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.invoices.invoices.v4.invoice',
      method: 'POST' as any,
      methodFqn:
        'wix.invoices.invoices.v4.Invoices.BulkUpdateInvoiceTagsByFilter',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixInvoicesInvoicesV4InvoicesUrl({
        protoPath: '/v4/bulk/invoices/update-tags-by-filter',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __bulkUpdateInvoiceTagsByFilter;
}
